/**
 * Dashboard interactions on the Design Builder canvas and in Preview — the
 * same behaviour as the DVM Layout Builder, on the same building blocks:
 *
 * - input controls (dropdown / slider / input box / file upload) recompute
 *   their targets through `DvmInputComputeService` (custom API calls included);
 * - results are applied by the owning family's build step, exactly as in the
 *   Layout Builder (`DesignBuildHostComponent.applyRecomputeResult`);
 * - cross-filter and dropdown filters run client-side via the shared
 *   `dvm-tile-crossfilter.utils`, waterfalls recompute server-side.
 *
 * Interaction results are LIVE state (keyed by node id), never written into
 * the design document: playing with a filter must not edit the saved design.
 */

import { DesignWidgetApiService, widgetApiResult } from './design-widget-api.service';
import { HttpClient, HttpHeaders, type HttpResponse } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { concat, forkJoin, of, Subject, type Observable } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil, tap, toArray } from 'rxjs/operators';

import type { BuilderLayoutConfig } from '../../shared/self-service-config.types';
import { buildSelfServiceApiUrl, getSelfServicePlatformApiUrl, resolveSelfServiceApiUrl } from '../../shared/self-service-api.utils';
import { buildExcelExportRequest, excelExportFileOf, excelExportFilters, type ExcelExportFilter } from '../../shared/platform-excel-export.utils';
import { buildKpiWidgetData, recomputeEntryHasData, resolveRecomputeChartType } from '../../shared/self-service-recompute.utils';
import { CLIENT_CHART_CROSS_FILTER, type DashboardTile, type WaterfallBar } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { DvmInputComputeService } from '../../dvm-layout-builder/dvm-input-compute.service';
import { buildApiCallBody, buildApiCallParams, fileToBase64, findRecomputeTargetTile, otherActiveInputValues, sideEffectInputsFor } from '../../dvm-layout-builder/dvm-input-api-call.utils';
import {
  applyCrossFilterToClientTiles, effectiveListenerTileIds, effectiveTileFilterKey,
  isChartTile, isRecomputeCrossFilterTile, tileDatasets,
} from '../../dvm-layout-builder/dvm-tile-crossfilter.utils';
import { applyStepSignsToWaterfallBars, buildWaterfallPreviewOption, buildWaterfallTileData } from '../../components/waterfall-step/waterfall-tile.utils';
import {
  dropdownOptionsFromComputeResponse, normalizeDropdownOptionOrderMode, orderDropdownOptions,
  dataDropdownOptions, pickableDropdownOptions, staticDropdownOptions, withStaticOptions,
} from '../../components/dropdown-step/dropdown-options.utils';
import { SelfServiceDatasetService } from '../../shared/self-service-dataset.service';
import type { CanvasFilter, TextDataBinding } from '../design-builder.types';
import { APPROVAL_TABLE_TYPES } from '../design-builder.constants';
import { DesignDocumentStore } from '../state/design-document.store';
import { STATUS_COUNT_AGGREGATION, statusCountOf } from '../utils/design-status-count.utils';
import { inputDataBindingOf, inputDataComponentId } from '../utils/design-input-data.utils';
import { currencySymbol, isCurrencyExchange, isValueOnlyDropdown, type DisplayCurrency } from '../utils/design-currency.utils';
import { inputSaveConfigOf, typedSaveValue, updateRequestBody, VALUE_SOURCE_LAYOUT_TYPES } from '../utils/design-save-request.utils';

import { WIDGET_ROW_KEYS } from '../utils/design-publish.utils';
import { autoRefreshSecondsOf } from '../utils/design-refresh.utils';
import { canvasFilterConditions, isCanvasFilterActive, type CanvasFilterCondition } from '../utils/design-canvas-filter.utils';
import { KeycloakService } from 'keycloak-angular';
import { resolveWidgetData } from '../renderers/design-widget-data.utils';
import { groupNavigationFilters, navigationFilterQueryParams, sameFilterColumn, type NavigationFilterEntry } from '../../../shared-services/navigation-filters.utils';
import {
  BUTTON_DEFAULTS, buttonConfigOf, filterValuesOf, type ButtonApiCall, type ButtonConfig,
} from '../../../global-components/dvm-action-button/dvm-action-button.types';

import { boundTextComponents, chartTargets, tileDataset, tileFromNode, tileRenderData, widgetNodes } from './design-tiles.utils';
import {
  applyZoneSliderBinding, zoneSliderBindingOf, zoneSliderMeasureBindings, zoneSliderStaticData,
  type ZoneSliderBinding, type ZoneSliderMeasureKey, type ZoneSliderMeasures,
} from '../renderers/zone-slider-binding.utils';
import { resolveZoneSliderModel, type ZoneSliderData } from '../../../global-components/zone-slider/zone-slider.utils';


/** What the runtime needs from the build host: the family steps' result appliers. */
export interface RecomputeApplier {
  applyRecomputeResult(tile: DashboardTile, entry: Record<string, unknown>): boolean;
}

type CrossFilter = { column: string; values: string[]; sourceTileId: string };
type CrossFilterEvent = { column: string; value: string | null; values?: string[] };
/** Widgets that show a value, not a chart: they keep every filter (see filtersComponent). */
const VALUE_WIDGET_LAYOUTS = new Set(['ZONE_SLIDER', 'KPI']);
/** How long a filter cascade may run before what it holds back loads anyway (see armCascadeTimeout). */
const CASCADE_TIMEOUT_MS = 30000;
/** Joins a `col:` limit and the component it belongs to into one column-value key. */
const COLUMN_OWNER_SEPARATOR = '@@';
/** A value an auto-fill chose for a dropdown ([] = clear it). */
type AutoPick = { id: string; values: string[] };
/** One request of a filter cascade (see `changeDropdown`). */
type DropdownStep = {
  basePayload?: Record<string, unknown>;
  tileOverride?: DashboardTile;
  chain?: ReadonlySet<string>;
  alongside?: ReadonlyArray<string>;
  cascade: number;
  /** The last filter is set: recompute charts too, fill nothing more. */
  final?: boolean;
};

const LEGACY_GENERATE_PATH = '/api/dvm/self-service/waterfall/generate';

@Injectable()
export class DesignRuntimeService {
  private readonly doc = inject(DesignDocumentStore);
  private readonly http = inject(HttpClient);
  private readonly widgetApi = inject(DesignWidgetApiService);
  private readonly inputCompute = inject(DvmInputComputeService);
  private readonly datasets = inject(SelfServiceDatasetService);
  private readonly router = inject(Router);

  /**
   * Tiles already asked to hydrate, by tile id.
   *
   * Kept after the response, not cleared on it: `hydrate()` runs again whenever
   * the layout or the applier lands, and a tile whose compute returned nothing
   * usable would be POSTed again on each of those — one request per component,
   * repeatedly. Cleared only by `reset()`, i.e. per document.
   */
  private readonly hydrating = new Set<string>();
  /** Each dropdown's values from its last data answer (typed options aside) - what an automatic pick chooses from. */
  private readonly dataOptions = new Map<string, string[]>();

  /** Compute values for texts registered as KPI components, by componentId. */
  private readonly textValuesState = signal<Record<string, TextValueState>>({});
  readonly textValues = this.textValuesState.asReadonly();
  /**
   * The last value committed in each input widget (Enter / blur), by node id —
   * kept for every input, built or not, so text formulas can read it.
   */
  private readonly inputValuesState = signal<Record<string, number | string>>({});
  readonly inputValues = this.inputValuesState.asReadonly();

  /**
   * Input boxes filled from data ("Fill from data": a dataset column, summarised
   * and filtered by the dropdowns), by node id. Typing over the box wins until
   * the next filter change brings a new value.
   */
  private readonly inputDataValuesState = signal<Record<string, TextValueState>>({});
  readonly inputDataValues = this.inputDataValuesState.asReadonly();
  /** Dropdowns with "Currency exchange" ticked (Data tab), by node id. */
  readonly currencyExchangeIds = computed(() => new Set(widgetNodes(this.doc.document())
    .filter((node) => isCurrencyExchange(node)).map((node) => node.id)));
  /** Dropdowns with "Show the value only" ticked (Data tab): a pick is kept and shown, nothing else changes. */
  readonly valueOnlyIds = computed(() => new Set(widgetNodes(this.doc.document())
    .filter((node) => isValueOnlyDropdown(node)).map((node) => node.id)));
  /** Dropdowns whose value is not a filter: currency exchange and value-only. */
  private localOnlyIds(): Set<string> {
    return new Set([...this.currencyExchangeIds(), ...this.valueOnlyIds()]);
  }

  /**
   * Layer id → the currency it is shown in. A "Currency exchange" dropdown's
   * selected value is the rate; every target it hits (charts, tables, texts)
   * is shown × that rate instead of being filtered. The currency code (for
   * the symbol) is the value of the dropdown that targets it, e.g. View in.
   */
  private readonly currencyByNode = computed(() => {
    const out = new Map<string, DisplayCurrency>();
    const exchangeIds = this.currencyExchangeIds();
    if (!exchangeIds.size) return out;
    const tiles = this.tiles();
    const nodes = widgetNodes(this.doc.document());
    // componentId → layer ids (a widget's own, a text's registered one).
    const layersOf = new Map<string, string[]>();
    const addLayer = (componentId: string | undefined, nodeId: string) => {
      if (!componentId) return;
      layersOf.set(componentId, [...(layersOf.get(componentId) ?? []), nodeId]);
    };
    nodes.forEach((node) => addLayer(node.widget?.componentId ?? String(node.widget?.build?.['componentId'] ?? ''), node.id));
    tiles.forEach((tile) => addLayer(tile.componentId, tile.id));
    boundTextComponents(this.doc.document()).forEach((node, componentId) => addLayer(componentId, node.id));

    tiles.filter((tile) => exchangeIds.has(tile.id) && tile.dropdownControl).forEach((tile) => {
      const rate = Number(tile.dropdownControl!.value?.[0]);
      if (!tile.dropdownControl!.value?.length || !Number.isFinite(rate)) return;
      const source = tiles.find((other) => other.id !== tile.id && !exchangeIds.has(other.id) && other.dropdownControl
        && dropdownTargetsOf(other).has(tile.componentId));
      const code = String(source?.dropdownControl?.value?.[0] ?? '');
      const currency: DisplayCurrency = { code, symbol: code ? currencySymbol(code) : '', rate };
      dropdownTargetsOf(tile).forEach((componentId) => (layersOf.get(componentId) ?? [componentId]).forEach((id) => out.set(id, currency)));
      [...textTargetsOf(tile), ...(tile.dropdownControl!.targets ?? []).filter((id) => id.startsWith(TEXT_TARGET_PREFIX))
        .map((id) => id.slice(TEXT_TARGET_PREFIX.length))].forEach((id) => out.set(id, currency));
    });
    return out;
  });

  /**
   * The other inputs' values sent with a compute call - minus "Currency
   * exchange" dropdowns: their value is a rate, not a filter. Sent along, the
   * rate picked for CAD made the AUD refresh look for AUD rows with CAD's rate,
   * found none, and the exchange dropdown kept the stale rate.
   */
  private otherInputsFor(tiles: DashboardTile[], componentId: string): ReturnType<typeof otherActiveInputValues> {
    const localIds = this.localOnlyIds();
    if (!localIds.size) return otherActiveInputValues(tiles, componentId);
    const skip = new Set(tiles.filter((tile) => localIds.has(tile.id)).map((tile) => tile.componentId));
    return otherActiveInputValues(tiles, componentId).filter((input) => !skip.has(input.componentId));
  }

  /** The currency a layer is shown in (null = values as stored). */
  currencyFor(nodeId: string): DisplayCurrency | null {
    return this.currencyByNode().get(nodeId) ?? null;
  }

  /**
   * The rate selected in the dashboard's "Currency exchange" dropdown (the first
   * one with a value). Tables use it for every column ticked "Convert currency",
   * whether or not the exchange dropdown targets the table - the tick is the opt-in.
   */
  readonly activeCurrency = computed<DisplayCurrency | null>(() => {
    const exchangeIds = this.currencyExchangeIds();
    if (!exchangeIds.size) return null;
    const tiles = this.tiles();
    for (const tile of tiles) {
      if (!exchangeIds.has(tile.id) || !tile.dropdownControl?.value?.length) continue;
      const rate = Number(tile.dropdownControl.value[0]);
      if (!Number.isFinite(rate)) continue;
      const source = tiles.find((other) => other.id !== tile.id && !exchangeIds.has(other.id) && other.dropdownControl
        && dropdownTargetsOf(other).has(tile.componentId));
      const code = String(source?.dropdownControl?.value?.[0] ?? '');
      return { code, symbol: code ? currencySymbol(code) : '', rate };
    }
    return null;
  });

  /** A table's currency: the exchange that targets it, else the dashboard's active one. */
  currencyForTable(nodeId: string): DisplayCurrency | null {
    return this.currencyFor(nodeId) ?? this.activeCurrency();
  }

  /**
   * The value picked in a dropdown that has not been generated yet (no tile):
   * kept here so the dropdown still shows what was picked.
   */
  private readonly localDropdownState = signal<Record<string, string | string[]>>({});
  readonly localDropdownValues = this.localDropdownState.asReadonly();

  /** Request sequence per input box: only the latest call may write its value. */
  private readonly inputDataSeq = new Map<string, number>();

  /**
   * What users typed / picked in table cells (input and dropdown columns), by
   * table node id — one entry per edited cell, the latest value winning. Sent
   * to action buttons as the `{tableEdits}` token.
   */
  private readonly tableEditsState = signal<Record<string, TableCellEdit[]>>({});
  readonly tableEdits = this.tableEditsState.asReadonly();

  recordTableEdit(nodeId: string, edit: Omit<TableCellEdit, 'table'>): void {
    const table = widgetNodes(this.doc.document()).find((node) => node.id === nodeId)?.name ?? nodeId;
    this.tableEditsState.update((all) => {
      const same = (item: TableCellEdit) => item.field === edit.field && editRowKey(item) === editRowKey(edit);
      const rest = (all[nodeId] ?? []).filter((item) => !same(item));
      return { ...all, [nodeId]: [...rest, { ...edit, table }] };
    });
  }

  /** Compute values for zone sliders (value / min / max / cost), by node id. */
  private readonly zoneValuesState = signal<Record<string, ZoneSliderValueState>>({});
  readonly zoneValues = this.zoneValuesState.asReadonly();

  private layout: BuilderLayoutConfig | null = null;
  private applier: RecomputeApplier | null = null;

  /** Live tiles changed by interactions, keyed by node id (= tile id). */
  private readonly live = signal<ReadonlyMap<string, DashboardTile>>(new Map());
  /** componentIds currently recomputing. */
  readonly busy = signal<ReadonlySet<string>>(new Set());
  /**
   * Node ids whose FIRST data fetch (on open) is still in flight. Until it
   * answers, the widget's stored data is empty, so the shared component would
   * say "No data available" — the host shows a loading state instead.
   */
  private readonly pendingState = signal<ReadonlySet<string>>(new Set());
  readonly pending = this.pendingState.asReadonly();
  /** Charts whose latest answer had no rows for the active filters: they show No data. */
  private readonly noDataState = signal<ReadonlySet<string>>(new Set());
  readonly noData = this.noDataState.asReadonly();
  readonly uploadMessages = signal<Record<string, string>>({});
  readonly dashboardError = signal('');
  private dashboardRefreshVersion = 0;
  /** Chart id -> the refresh whose answer it is waiting for (see refreshDashboard). */
  private readonly tileRefreshVersions = new Map<string, number>();
  private readonly resetRequests = new Subject<void>();
  private resetVersion = 0;
  private readonly firstValueRequestVersions = new Map<string, number>();
  /** Filter cascades: the newest one's token, and the one still filling (0 = none). */
  private cascadeToken = 0;
  private activeCascade = 0;
  /** Charts / texts / sliders asked to load while a cascade was filling: they load at its end. */
  private loadAfterCascade = false;
  /** Non-dropdown targets of the dropdowns a cascade set, and those a request of it answered. */
  private readonly cascadeTargets = new Set<string>();
  private readonly cascadeAnswered = new Set<string>();
  private cascadeTimer?: ReturnType<typeof setTimeout>;

  /**
   * A cascade that has not finished after a while (a step whose answer never
   * came or was dropped) is ended, so what it held back loads with the filters
   * set so far - the screen never stays "Loading" for good. Each step re-arms it.
   */
  private armCascadeTimeout(cascade: number): void {
    clearTimeout(this.cascadeTimer);
    this.cascadeTimer = setTimeout(() => {
      if (this.activeCascade !== cascade) return;
      console.warn('[design] A filter cascade did not finish in time; loading the screen with the filters set so far.');
      this.endCascade(cascade);
    }, CASCADE_TIMEOUT_MS);
  }

  /**
   * Orders answers that can overlap. Every interaction recompute (dropdown,
   * input, upload, button) stamps the components it asks for; an open-time
   * fetch or a refresh notes the clock when it starts. A refresh answer for a
   * component an interaction has asked for since is dropped: the interaction's
   * answer is newer and carries the user's selection. Without this a slow,
   * unfiltered refresh could land after a dropdown's recompute and undo it.
   */
  private requestClock = 0;
  private readonly componentStamps = new Map<string, number>();
  /** An interaction that named no targets may answer for any component. */
  private anyComponentStamp = 0;
  /** The newest interaction's stamp: only its answer may clear the "Updating data…" overlay. */
  private latestInteractionStamp = 0;
  /** Refresh requests still in flight (auto-refresh skips a tick while > 0). */
  private refreshesInFlight = 0;
  /** A quiet refresh was asked for before the layout / applier existed: run it once they do. */
  private quietRefreshWaiting = false;
  /** Tile id -> the refresh that put it in `pending` (only that refresh's answer clears it). */
  private readonly pendingRefreshOwners = new Map<string, number>();
  /** Tiles whose live data came only from a refresh, not an interaction (see `dropRefreshedData`). */
  private readonly refreshedTiles = new Set<string>();
  /**
   * Every active selection (dropdown values + chart cross-filter), for widgets
   * that filter their own rows client-side — e.g. data-bound text — instead of
   * being recompute targets.
   */
  readonly activeFilters = signal<ReadonlyArray<ActiveFilter>>([]);
  private navigationFilter: DashboardNavigationFilter | null = null;
  /** Dropdown node id -> the incoming filter key it shows (and the values it got). */
  private navigationDropdowns = new Map<string, { key: string; values: string[] }>();
  /** Incoming filter keys the user has since changed / cleared in their dropdown: no longer applied. */
  private navigationOverridden = new Set<string>();
  readonly navigationReset = signal(0);

  /** Incoming table navigation is scoped to the requested component IDs. */
  setNavigationFilter(filter: DashboardNavigationFilter | null): void {
    // Cancel the previous route's requests and restore definition data before
    // applying a changed row filter, including same-dashboard navigation.
    this.reset();
    this.navigationFilter = filter;
    // Already reset and applied for this design: its "design opened" effect need not do it again.
    this.navigationAppliedFor = this.doc.document().id;
    this.applyNavigationSelection();
    if (this.applier && this.layout) this.refreshDashboard();
  }

  /** The design `setNavigationFilter` last reset and applied the filter for (see the documentId effect). */
  private navigationAppliedFor: string | null = null;

  /** Navigation-selected dropdowns whose "Show first value" fill still has to run (needs the layout). */
  private pendingNavigationFirstValues: Array<{ id: string; values: string[] }> = [];

  /**
   * Dropdowns for the incoming columns select those values themselves, so the
   * user sees (and can change / clear) the filter instead of a hidden one; the
   * "Show first value" fill runs once the layout (compute URL) is known.
   */
  private applyNavigationSelection(): void {
    this.pendingNavigationFirstValues = this.selectNavigationDropdowns();
    this.publishFilters();
    this.flushNavigationFirstValues();
  }

  private flushNavigationFirstValues(): void {
    if (!this.layout || !this.pendingNavigationFirstValues.length) return;
    const selected = this.pendingNavigationFirstValues;
    this.pendingNavigationFirstValues = [];
    this.runNavigationFirstValues(selected);
  }

  /**
   * Select every dropdown whose column an incoming filter names - its
   * "Fill from the screen that opened this one" setting, else the same column
   * (names compared loosely). Returns them in incoming order.
   */
  private selectNavigationDropdowns(): Array<{ id: string; values: string[] }> {
    this.navigationDropdowns = new Map();
    this.navigationOverridden = new Set();
    const nav = this.navigationFilter;
    const grouped = nav ? groupNavigationFilters(navigationEntriesOf(nav)) : new Map<string, string[]>();
    if (!grouped.size) return [];
    const order = [...grouped.keys()];
    const selected: Array<{ id: string; values: string[]; rank: number }> = [];
    const commits: DashboardTile[] = [];
    for (const tile of this.tiles()) {
      const control = tile.dropdownControl;
      if (!control) continue;
      const mapping = String(tile.payload?.['navigationFilterKey'] ?? '').trim();
      if (mapping === '__off__') continue;
      const column = mapping || control.field || '';
      const key = order.find((name) => name === column) ?? order.find((name) => sameFilterColumn(name, column));
      if (!key) continue;
      const incoming = grouped.get(key) ?? [];
      const values = tile.payload?.['isMultiSelect'] === true ? incoming : incoming.slice(0, 1);
      if (!values.length) continue;
      this.navigationDropdowns.set(tile.id, { key, values });
      commits.push({ ...tile, payload: { ...tile.payload, value: values }, dropdownControl: { ...control, value: values } });
      if (control.field && tile.componentId && !this.valueOnlyIds().has(tile.id)) {
        this.dropdownFilters.set(tile.componentId, { field: control.field, values, textTargets: textTargetsOf(tile) });
      }
      selected.push({ id: tile.id, values, rank: order.indexOf(key) });
    }
    if (commits.length) this.commit(commits);
    return selected.sort((a, b) => a.rank - b.rank).map(({ id, values }) => ({ id, values }));
  }

  /**
   * A selected dropdown with "Show first value" fills its dropdowns as if the
   * user had picked it. The last one in incoming order (the most specific, e.g.
   * the spec after the customer) runs it; the dropdowns the navigation selected
   * itself are never overwritten by a first value.
   */
  private runNavigationFirstValues(selected: Array<{ id: string; values: string[] }>): void {
    if (!selected.length || !this.layout) return;
    const filled = new Set(selected.map((entry) => entry.id));
    // Else the most specific one that refreshes other dropdowns: they may
    // narrow to a single option (a spec's customer), which is then selected.
    const driver = [...selected].reverse().find((entry) => {
      const tile = this.tileFor(entry.id);
      return !!tile && this.firstValueDropdownIds(tile).size > 0;
    }) ?? [...selected].reverse().find((entry) => {
      const tile = this.tileFor(entry.id);
      return !!tile && this.dropdownTargetIds(tile).size > 0;
    });
    if (driver) this.onDropdownChange(driver.id, driver.values, undefined, undefined, filled);
  }

  /**
   * The filters a zone slider / "Fill from data" input box computes with: the
   * dashboard's dropdown and cross filters, plus every filter the opening
   * screen sent (table-row navigation) that no dropdown shows. Before, those
   * reached charts (navigationPayload) but not these widgets, so a slider
   * opened for another spec kept the first spec's numbers. Scoped like charts:
   * a navigation aimed at named components skips the others. Used by bound
   * texts too, which followed only the dropdowns before.
   */
  private widgetFilters(componentId: string): ActiveFilter[] {
    const active = this.filtersFor(componentId);
    const nav = this.navigationFilter;
    if (!nav || (nav.targetComponents.length && !nav.targetComponents.includes(componentId))) return active;
    // A "Show the value only" dropdown shows its incoming value but applies no
    // filter: that value still filters these widgets, as it does the charts.
    const valueOnly = this.valueOnlyIds();
    const heldByDropdown = new Set([...this.navigationDropdowns]
      .filter(([tileId]) => !valueOnly.has(tileId)).map(([, entry]) => entry.key));
    const entries = navigationEntriesOf(nav).filter((entry) => !this.navigationOverridden.has(entry.key) && !heldByDropdown.has(entry.key));
    for (const [field, values] of groupNavigationFilters(entries)) {
      if (values.length && !active.some((filter) => sameFilterColumn(filter.field, field))) active.push({ field, values, textTargets: [] });
    }
    return active;
  }

  /**
   * The components a dropdown lists as its targets in the DESIGN (its "Target
   * chart(s) / input(s)") - not the live payload, whose `targets` each request
   * overwrites. Empty = it lists none.
   */
  private designTargetsOf(source: DashboardTile): Set<string> {
    const node = widgetNodes(this.doc.document()).find((candidate) => candidate.id === source.id);
    const designed = node ? tileFromNode(node) : undefined;
    const ids = new Set<string>([...(designed?.dropdownControl?.targets ?? []), ...(source.dropdownControl?.targets ?? [])]);
    const payloadTargets = designed?.payload?.['targets'];
    if (Array.isArray(payloadTargets)) payloadTargets.forEach((target) => {
      const id = target && typeof target === 'object' ? String((target as Record<string, unknown>)['componentId'] ?? '') : '';
      if (id) ids.add(id);
    });
    // A text is targeted by its LAYER ("text:<node id>", or the build's textTargets):
    // it is the bound text's component that computes, so that is what it targets.
    const textNodes = [
      ...[...ids].filter((id) => id.startsWith(TEXT_TARGET_PREFIX)).map((id) => id.slice(TEXT_TARGET_PREFIX.length)),
      ...(designed ? textTargetsOf(designed) : []), ...textTargetsOf(source),
    ];
    if (textNodes.length) {
      const componentOfNode = new Map([...boundTextComponents(this.doc.document())].map(([componentId, node]) => [node.id, componentId]));
      textNodes.forEach((nodeId) => {
        const componentId = componentOfNode.get(nodeId);
        if (componentId) ids.add(componentId);
      });
    }
    return ids;
  }

  /** Every component some dropdown lists as a target (see designTargetsOf). */
  private explicitlyTargeted(): Set<string> {
    const out = new Set<string>();
    this.tiles().filter((tile) => tile.dropdownControl).forEach((tile) => this.designTargetsOf(tile).forEach((id) => out.add(id)));
    return out;
  }

  /** The charts / tables on the screen - the components whose filters follow their targets. */
  private targetedChartIds(): Set<string> {
    return new Set(this.tiles()
      .filter((tile) => isChartTile(tile) && !VALUE_WIDGET_LAYOUTS.has(tile.layoutType))
      .map((tile) => tile.componentId).filter(Boolean));
  }

  /** Every component a dropdown can list as a target: the screen's widgets and bound texts. */
  private targetableIds(): Set<string> {
    return new Set([
      ...this.tiles().map((tile) => tile.componentId),
      ...boundTextComponents(this.doc.document()).keys(),
      ...this.zoneSliderComponents().map(({ componentId }) => componentId),
    ].filter(Boolean));
  }

  /**
   * Whether the dropdown `sourceComponentId` filters `componentId`:
   * - it lists it as a target, or lists no targets at all (a dashboard-wide filter);
   * - a component NO dropdown targets is filtered by every dropdown, as before;
   * - a helper request no dropdown can target (a "Color by value" limit …) keeps every filter.
   * Only a component some dropdowns do target is limited to those.
   */
  private filtersComponent(sourceComponentId: string, componentId: string): boolean {
    const source = this.tiles().find((tile) => tile.componentId === sourceComponentId);
    if (!source) return true;
    if (!this.targetableIds().has(componentId)) return true;
    // Only charts / tables follow their targets. A value - a text, a zone slider,
    // an input box, a KPI - keeps every filter and refreshes on every change, as
    // it always did (a slider limited to the customer showed every spec's price).
    if (!this.targetedChartIds().has(componentId)) return true;
    const targets = this.designTargetsOf(source);
    if (!targets.size || targets.has(componentId)) return true;
    return !this.explicitlyTargeted().has(componentId);
  }

  /** The dropdown and cross filters `componentId` computes with: only those of the dropdowns that target it. */
  private filtersFor(componentId: string): ActiveFilter[] {
    const out: ActiveFilter[] = [];
    this.dropdownFilters.forEach((filter, sourceComponentId) => {
      if (filter.field && filter.values.length && this.filtersComponent(sourceComponentId, componentId)) out.push(filter);
    });
    if (this.crossFilter) {
      const source = this.tileFor(this.crossFilter.sourceTileId);
      out.push({ field: this.crossFilter.column, values: this.crossFilter.values, textTargets: source ? textTargetsOf(source) : [] });
    }
    return out;
  }

  /** The datasets a chart's filters must name: from its definition, else the dataset its widget is bound to. */
  private datasetsForFilters(tile: DashboardTile): string[] {
    const fromDefinition = filterDatasets(tile);
    if (fromDefinition.length) return fromDefinition;
    const bound = widgetNodes(this.doc.document()).find((node) => node.id === tile.id)?.widget?.binding?.['dataset'];
    return typeof bound === 'string' && bound ? [bound] : [];
  }

  /** A chart's compute payload: its stored request, the opening screen's filters and its canvas filters. */
  private navigationPayload(tile: DashboardTile): Record<string, unknown> {
    return this.withCanvasFilters(tile.componentId, this.incomingPayload(tile), this.datasetsForFilters(tile));
  }

  private incomingPayload(tile: DashboardTile): Record<string, unknown> {
    // New nested tables persist one clean TABLE request per level. Hydration and
    // Refresh must execute only level 0; later levels are fetched by the table
    // renderer as their parent rows are expanded.
    const storedPayload = initialChartComputePayload(tile);
    const nav = this.navigationFilter;
    if (!nav || (nav.targetComponents.length && !nav.targetComponents.includes(tile.componentId))) return storedPayload;
    // Every incoming filter filters the data - whichever path loads the chart
    // (first hydrate or refresh) - until the user changes / clears the dropdown
    // that shows it; a dropdown-held one is then applied through the dropdown.
    // A filter a dropdown shows follows that dropdown's targets: it filters this
    // chart only if the dropdown targets it.
    const heldBy = new Map([...this.navigationDropdowns].map(([tileId, held]) => [held.key, this.tileFor(tileId)?.componentId ?? '']));
    const entries = navigationEntriesOf(nav).filter((entry) => !this.navigationOverridden.has(entry.key)
      && (!heldBy.get(entry.key) || this.filtersComponent(heldBy.get(entry.key)!, tile.componentId)));
    if (!entries.length) return storedPayload;
    const grouped = groupNavigationFilters(entries);
    return { ...storedPayload, filterKey: entries[0].key, filterValue: entries[0].value,
      filters: [...((storedPayload['filters'] as unknown[]) ?? []),
        ...computeFilters([...grouped].map(([field, values]) => ({ field, values })), this.datasetsForFilters(tile))] };
  }

  /**
   * Builder edit canvas (not Preview, not published). Buttons still run their
   * download / API / filter there so they can be tried out, but never navigate
   * away from the design being edited.
   */
  readonly designMode = signal(false);

  /** Per-button run state (spinner, result line, filter applied), by node id. */
  private readonly buttonStatesState = signal<Record<string, ButtonRunState>>({});
  readonly buttonStates = this.buttonStatesState.asReadonly();

  /**
   * A button asked for another screen of this design. The published view and
   * the builder's Preview own the active screen, so they act on it; `seq`
   * makes a repeat click on the same screen still a change.
   */
  readonly screenRequest = signal<{ screenId: string; seq: number } | null>(null);

  /**
   * The screen shown as a popup over the page (an Open popup button whose
   * content is a screen designed on the canvas); null = none open. The
   * builder and the published view each render it with `app-design-screen-popup`.
   */
  readonly popupScreen = signal<{ screenId: string; buttonId: string; seq: number } | null>(null);

  private crossFilter: CrossFilter | null = null;
  private readonly dropdownFilters = new Map<string, { field: string; values: string[]; textTargets: string[] }>();
  private readonly waterfallSeq = new Map<string, number>();

  /** Every generated canvas widget as a tile, with live interaction state applied. */
  readonly tiles = computed<DashboardTile[]>(() => {
    const live = this.live();
    return widgetNodes(this.doc.document())
      .map((node) => live.get(node.id) ?? withoutSavedOptions(tileFromNode(node)))
      .filter((tile): tile is DashboardTile => !!tile);
  });

  /**
   * The open design's id as its own signal. An effect reading
   * `document().id` directly depends on the whole document, so every edit
   * (moving a box, changing a table type) re-ran it — wiping live data and
   * re-sending compute for every widget. A computed only notifies on a new id.
   */
  private readonly documentId = computed(() => this.doc.document().id);

  // ── Canvas filters ─────────────────────────────────────────────────────────

  /**
   * The canvas filters that apply (Canvas settings → Canvas filters), by the
   * component ids of the widgets and texts they target: a widget's component,
   * a bound text's, an input box's "Fill from data" value. Targets are stored
   * by node id, so a widget regenerated under a new component id stays filtered.
   */
  private readonly canvasFiltersByComponent = computed(() => {
    const out = new Map<string, CanvasFilter[]>();
    const filters = (this.doc.canvas().filters ?? []).filter(isCanvasFilterActive);
    if (!filters.length) return out;
    const document = this.doc.document();
    const componentsOf = new Map<string, Set<string>>();
    const add = (nodeId: string, componentId: string | undefined) => {
      if (componentId) componentsOf.set(nodeId, (componentsOf.get(nodeId) ?? new Set<string>()).add(componentId));
    };
    widgetNodes(document).forEach((node) => {
      add(node.id, node.widget?.componentId);
      add(node.id, String(node.widget?.build?.['componentId'] ?? ''));
      if (inputDataBindingOf(node)) add(node.id, inputDataComponentId(node.id));
    });
    this.tiles().forEach((tile) => add(tile.id, tile.componentId));
    boundTextComponents(document).forEach((node, componentId) => add(node.id, componentId));
    filters.forEach((filter) => filter.targets.forEach((nodeId) => componentsOf.get(nodeId)?.forEach((componentId) => {
      out.set(componentId, [...(out.get(componentId) ?? []), filter]);
    })));
    return out;
  });

  /** The conditions of the canvas filters that target `componentId`, for a request reading `datasets`. */
  private canvasConditions(componentId: string, datasets: ReadonlyArray<string>): CanvasFilterCondition[] {
    return (this.canvasFiltersByComponent().get(componentId) ?? []).flatMap((filter) => canvasFilterConditions(filter, datasets));
  }

  /** `payload` with the canvas filters that target `componentId` as `canvasFilters` (applied, never stored). */
  private withCanvasFilters(componentId: string, payload: Record<string, unknown>, datasets: ReadonlyArray<string>): Record<string, unknown> {
    const conditions = this.canvasConditions(componentId, datasets);
    return conditions.length ? { ...payload, canvasFilters: conditions } : payload;
  }

  /**
   * A recompute request (dropdown, input, filter button, options list) with each
   * target's canvas filters as `targetCanvasFilters`: the backend applies them
   * to that target only, for this request only.
   */
  private withTargetCanvasFilters(payload: Record<string, unknown>, targetIds: ReadonlyArray<string>): Record<string, unknown> {
    const byTarget: Record<string, CanvasFilterCondition[]> = {};
    targetIds.forEach((componentId) => {
      const conditions = this.canvasConditions(componentId, this.componentDatasets(componentId));
      if (conditions.length) byTarget[componentId] = conditions;
    });
    return Object.keys(byTarget).length ? { ...payload, targetCanvasFilters: byTarget } : payload;
  }

  /** The datasets a component reads: a widget's (see datasetsForFilters), a bound text's. */
  private componentDatasets(componentId: string): string[] {
    const tile = this.tiles().find((candidate) => candidate.componentId === componentId);
    if (tile) return this.datasetsForFilters(tile);
    const text = boundTextComponents(this.doc.document()).get(componentId);
    return text?.dataBinding?.dataset ? [text.dataBinding.dataset] : [];
  }

  /** The canvas filter conditions of a widget node - a nested table adds them to the child levels it loads. */
  canvasFiltersForNode(nodeId: string): CanvasFilterCondition[] {
    const tile = this.tileFor(nodeId);
    return tile ? this.canvasConditions(tile.componentId, this.datasetsForFilters(tile)) : [];
  }

  /** The component ids the canvas filters targeted when they were last applied (see reloadForCanvasFilters). */
  private canvasFilterTargets = new Set<string>();

  /**
   * The canvas filters changed (edited on the canvas): every widget and text
   * they targeted before or target now reloads with them - a dropdown its
   * options. The rest of the dashboard is left as it is.
   */
  private reloadForCanvasFilters(): void {
    const now = new Set(this.canvasFiltersByComponent().keys());
    const affected = new Set([...this.canvasFilterTargets, ...now]);
    this.canvasFilterTargets = now;
    if (!affected.size || !this.layout) return;
    this.tiles().filter((tile) => tile.dropdownControl && affected.has(tile.componentId)).forEach((tile) => this.reloadDropdownOptions(tile.id));
    this.refreshDashboard(false, { quiet: true, only: affected });
  }

  constructor() {
    // Another design opened → its interactions start from scratch, then
    // anything published without data fetches it.
    effect(() => {
      const id = this.documentId();
      untracked(() => {
        // The published view loads the design and THEN sets its filter, which
        // already reset and applied it for this design: running it again would
        // cancel and repeat every request (the whole filter fill twice).
        const applied = this.navigationAppliedFor === id;
        this.navigationAppliedFor = null;
        if (applied) {
          this.hydrate();
          return;
        }
        this.reset();
        // The published view loads the design and THEN sets the incoming
        // filter, synchronously; this reset runs after both and wiped the
        // dropdowns the navigation had just selected. Select them again.
        if (this.navigationFilter) this.applyNavigationSelection();
        this.hydrate();
      });
    });

    // Canvas filters edited: what they target reloads with them. Opening a
    // design is not a change - its widgets load with its filters anyway.
    let canvasFiltersSeen: { documentId: string; signature: string } | null = null;
    effect(() => {
      const documentId = this.documentId();
      const signature = JSON.stringify((this.doc.canvas().filters ?? []).filter(isCanvasFilterActive));
      untracked(() => {
        const changed = canvasFiltersSeen?.documentId === documentId && canvasFiltersSeen.signature !== signature;
        if (!changed) this.canvasFilterTargets = new Set(this.canvasFiltersByComponent().keys());
        canvasFiltersSeen = { documentId, signature };
        if (changed) this.reloadForCanvasFilters();
      });
    });

    // Auto-refresh (Design tab, nothing selected → Data refresh): re-read the
    // database on the dashboard's interval while it is shown - Preview and the
    // published view, never the canvas being edited. The effect restarts the
    // timer when the interval or the mode changes, and stops it on destroy.
    effect((onCleanup) => {
      const seconds = autoRefreshSecondsOf(this.doc.canvas().autoRefreshSeconds);
      if (!seconds || this.designMode()) return;
      const timer = setInterval(() => this.autoRefreshTick(), seconds * 1000);
      onCleanup(() => clearInterval(timer));
    });
  }

  setLayout(layout: BuilderLayoutConfig | null): void {
    this.layout = layout;
    // The opening screen's filters start filling first, so the charts wait for them.
    this.flushNavigationFirstValues();
    this.hydrate();
    this.flushQuietRefresh();
  }
  attach(applier: RecomputeApplier | null): void { this.applier = applier; this.hydrate(); this.flushQuietRefresh(); }

  private flushQuietRefresh(): void {
    if (this.quietRefreshWaiting && this.layout && this.applier) this.refreshDashboard(false, { quiet: true });
  }

  /**
   * Compute a bound text's value.
   *
   * A text IS a KPI — dataset, field, aggregation — so it posts a KPI-shaped
   * payload to `/components/compute`. One call: the definition travels in the
   * body, and the backend registers the component from it, so no separate
   * write to the component store is needed.
   */
  connectText(componentId: string, binding: TextDataBinding, title: string, refresh?: QuietRefresh): void {
    // No layout yet = no compute URL: posting now would hit the legacy path
    // (404). hydrateTexts() connects every text as soon as the layout lands.
    if (!this.layout) return;
    // Filters still filling: the text loads once they are set (see endCascade).
    if (this.heldForCascade()) return;
    const payload = this.withCanvasFilters(componentId, withFilters({ ...textComputePayload(componentId, binding, title),
      // The backend's dashboardId is a number (Long): the configured one, as every
      // other component sends — never the design's own "dsg_…" id, which it rejects.
      ...(binding.aggregation === STATUS_COUNT_AGGREGATION && typeof this.layout.defaults?.dashboardId === 'number'
        ? { dashboardId: this.layout.defaults.dashboardId }
        : {}) }, computeFilters(this.widgetFilters(componentId), [binding.dataset ?? ''])), [binding.dataset ?? '']);
    // A quiet refresh keeps the value on screen until the new one lands.
    const shown = refresh?.quiet && this.textValuesState()[componentId]?.status === 'ready';
    if (!shown) this.patchTextValue(componentId, { status: 'loading' });
    const request$ = this.http.post<Record<string, unknown>>(this.generateUrl(), payload);
    (refresh ? this.trackRefresh(request$) : request$)
      .pipe(catchError(() => of(null)), takeUntil(this.resetRequests))
      .subscribe((response) => {
        // An interaction recomputed this text after the refresh started: its answer is newer.
        if (refresh && this.supersededSince(componentId, refresh.stamp)) return;
        if (!response) {
          // A failed quiet refresh leaves the last value up rather than an error.
          if (!shown) this.patchTextValue(componentId, { status: 'error', message: 'Could not compute this value.' });
          return;
        }
        // Compute answers with `{ results: [{ componentId, result }] }`, so the
        // envelope is unwrapped before the KPI extractor sees it.
        this.patchTextValue(componentId, textValueFromResult(binding, computeEntryFor(response, componentId)));
      });
  }

  /** Re-fetch every registered text (the Data tab's refresh button, and every dashboard refresh). */
  refreshTexts(refresh?: QuietRefresh, only?: ReadonlySet<string>): void {
    boundTextComponents(this.doc.document()).forEach((node, componentId) => {
      if (node.dataBinding && (!only || only.has(componentId))) this.connectText(componentId, node.dataBinding, node.name ?? '', refresh);
    });
  }

  private patchTextValue(componentId: string, state: TextValueState): void {
    this.textValuesState.update((all) => ({ ...all, [componentId]: state }));
  }

  /**
   * Generate / Update Configuration for a zone slider.
   *
   * Each mapped measure (value, minimum, maximum, cost basis) is ONE compute
   * component — `<componentId>-value`, `-min`, … — posted in the same
   * TEXT-shaped definition a bound text uses, so the backend registers and
   * aggregates it like any chart. The four answers land in `zoneValues()` and
   * the widget host overlays them on the slider's static configuration.
   */
  connectZoneSlider(nodeId: string, componentId: string, binding: ZoneSliderBinding, title: string, refresh?: QuietRefresh): void {
    if (!this.layout || this.heldForCascade()) return;
    const measures = zoneSliderMeasureBindings(binding);
    // The record the slider stands for (MAX of a text column = its value for one record).
    const record = binding.dataset && binding.recordIdField
      ? { key: 'recordId' as ZoneSliderMeasureKey, dataset: binding.dataset, field: binding.recordIdField, aggregation: 'MAX' }
      : null;
    if (!measures.length && !record) {
      this.patchZoneValue(nodeId, { status: 'ready', values: {} });
      return;
    }
    // A quiet refresh keeps the slider's values on screen until the new ones land.
    const shown = refresh?.quiet && this.zoneValuesState()[nodeId]?.status === 'ready';
    if (!shown) {
      this.patchZoneValue(nodeId, { status: 'loading' });
      this.markGenerating(nodeId, true);
    }
    const filters = this.widgetFilters(componentId);
    const calls = [...measures, ...(record ? [record] : [])].map((measure) => {
      const measureId = `${componentId}-${measure.key}`;
      // The dashboard's dropdown filters and the opening screen's row: the
      // slider follows the selected customer / spec.
      const payload = this.withCanvasFilters(componentId, withFilters(
        textComputePayload(measureId, { dataset: measure.dataset, field: measure.field, aggregation: measure.aggregation }, `${title || 'Zone slider'} ${measure.key}`),
        computeFilters(filters, [measure.dataset])), [measure.dataset]);
      return this.http.post<Record<string, unknown>>(this.generateUrl(), payload).pipe(
        map((response) => ({ measure, result: computeEntryFor(response, measureId) })),
        catchError(() => of({ measure, result: null as Record<string, unknown> | null })),
      );
    });
    const seq = (this.zoneSeq.get(nodeId) ?? 0) + 1;
    this.zoneSeq.set(nodeId, seq);
    const answers$ = forkJoin(calls);
    (refresh ? this.trackRefresh(answers$).pipe(takeUntil(this.resetRequests)) : answers$).subscribe((answers) => {
      if (!shown) this.markGenerating(nodeId, false);
      // A newer call (a filter change, or another refresh) owns the slider now.
      if (this.zoneSeq.get(nodeId) !== seq) return;
      const values: ZoneSliderMeasures = {};
      const errors: string[] = [];
      let recordId: string | undefined;
      answers.forEach(({ measure, result }) => {
        if (!result) { errors.push(`${measure.key}: could not compute`); return; }
        const error = computeEntryError(result);
        if (error) { errors.push(`${measure.key}: ${error}`); return; }
        if (measure.key === 'recordId') {
          const id = kpiValueOf(result, measure.field);
          if (id !== null && id !== undefined && String(id).trim()) recordId = String(id);
          else errors.push('recordId: no record for the current selection');
          return;
        }
        const value = Number(kpiValueOf(result, measure.field));
        if (Number.isFinite(value)) values[measure.key] = value;
        else errors.push(`${measure.key}: no numeric value returned`);
      });
      const answered = Object.keys(values).length || recordId || !errors.length;
      // A failed quiet refresh leaves the last values up rather than an error.
      if (!answered && shown) return;
      this.patchZoneValue(nodeId, answered
        ? { status: 'ready', values, ...(recordId ? { recordId } : {}), ...(errors.length ? { message: errors.join(' · ') } : {}) }
        : { status: 'error', message: errors.join(' · ') });
    });
  }

  /** Request sequence per zone slider: only the latest call may write its values. */
  private readonly zoneSeq = new Map<string, number>();

  /** Re-fetch every generated zone slider (the Data tab's refresh button, and quiet dashboard refreshes). */
  refreshZoneSliders(refresh?: QuietRefresh): void {
    this.zoneSliderComponents().forEach(({ node, componentId }) =>
      this.connectZoneSlider(node.id, componentId, zoneSliderBindingOf(node.widget?.binding), node.name ?? '', refresh));
  }

  /**
   * Fetch an input box's "Fill from data" value: one column of a dataset,
   * aggregated, with the dashboard's dropdown filters — posted like a bound text.
   * A new value replaces what the user typed, except on a quiet (auto) refresh.
   */
  connectInputData(nodeId: string, refresh?: QuietRefresh): void {
    if (!this.layout || this.heldForCascade()) return;
    const node = widgetNodes(this.doc.document()).find((item) => item.id === nodeId);
    const binding = inputDataBindingOf(node);
    if (!node || !binding) return;
    const componentId = inputDataComponentId(nodeId);
    const active = this.widgetFilters(componentId);
    const filters = computeFilters(active, [binding.dataset]);
    // "Show only when this filter is picked" (a column, e.g. customer_spec): the
    // box shows "-" unless THAT filter has a value - other filters do not count.
    // Without one, "Empty when no filter is picked" (on unless switched off):
    // with every filter cleared the box shows "-", not the whole dataset's value.
    const appearance = node.widget?.appearance ?? {};
    const required = String(appearance['requiredFilterField'] ?? '').trim().toLowerCase();
    const blank = required
      ? !active.some((filter) => filter.field.trim().toLowerCase() === required && filter.values.length)
      : !filters.length && appearance['emptyWithoutFilter'] !== false;
    if (blank) {
      this.inputDataSeq.set(nodeId, (this.inputDataSeq.get(nodeId) ?? 0) + 1);
      this.patchInputData(nodeId, { status: 'ready', value: null });
      if (this.inputValuesState()[nodeId] !== undefined) this.inputValuesState.update(({ [nodeId]: _typed, ...rest }) => rest);
      return;
    }
    const payload = this.withCanvasFilters(componentId, withFilters(
      textComputePayload(componentId, { dataset: binding.dataset, field: binding.field, aggregation: binding.aggregation }, node.name ?? ''),
      filters), [binding.dataset]);
    const shown = refresh?.quiet && this.inputDataValuesState()[nodeId]?.status === 'ready';
    if (!shown) this.patchInputData(nodeId, { status: 'loading' });
    const seq = (this.inputDataSeq.get(nodeId) ?? 0) + 1;
    this.inputDataSeq.set(nodeId, seq);
    const request$ = this.http.post<Record<string, unknown>>(this.generateUrl(), payload);
    (refresh ? this.trackRefresh(request$) : request$)
      .pipe(catchError(() => of(null)), takeUntil(this.resetRequests))
      .subscribe((response) => {
        if (this.inputDataSeq.get(nodeId) !== seq) return;
        if (!response) {
          if (!shown) this.patchInputData(nodeId, { status: 'error', message: 'Could not load this value.' });
          return;
        }
        const result = computeEntryFor(response, componentId);
        const error = computeEntryError(result);
        if (error) {
          if (!shown) this.patchInputData(nodeId, { status: 'error', message: error });
          return;
        }
        const raw = kpiValueOf(result, binding.field);
        const value = raw === null || raw === undefined || raw === '' ? null : (Number.isFinite(Number(raw)) ? Number(raw) : String(raw));
        this.patchInputData(nodeId, { status: 'ready', value });
        if (!refresh?.quiet && this.inputValuesState()[nodeId] !== undefined) {
          this.inputValuesState.update(({ [nodeId]: _typed, ...rest }) => rest);
        }
      });
  }

  /** Re-fetch every "Fill from data" input box. */
  refreshInputData(refresh?: QuietRefresh): void {
    this.inputDataNodes().forEach((node) => this.connectInputData(node.id, refresh));
  }

  /** Fetch "Fill from data" input boxes that have no value yet (open / publish). */
  private hydrateInputData(): void {
    if (!this.layout) return;
    this.inputDataNodes().forEach((node) => {
      if (!this.inputDataValuesState()[node.id]) this.connectInputData(node.id);
    });
  }

  private inputDataNodes(): ReturnType<typeof widgetNodes> {
    return widgetNodes(this.doc.document()).filter((node) => !!inputDataBindingOf(node));
  }

  // ── Column values (a "Color by value" limit read from a dataset column) ──

  /** A column's value for the current filters, by `col:` key; fetched on first ask. */
  private readonly columnValuesState = signal<Record<string, TextValueState>>({});
  private readonly columnValueSeq = new Map<string, number>();
  /** Column values asked for while a filter cascade was filling: fetched at its end. */
  private readonly heldColumnKeys = new Set<string>();

  /**
   * The number a dataset column has for the dashboard's current filters (the
   * selected customer / spec …), e.g. a record's floor price as a "Color by
   * value" limit. `key` is a `col:` limit (see columnLimitId). Null while it
   * loads or when there is none. Safe inside a computed: the first ask only
   * schedules the fetch.
   *
   * `ownerComponentId`: the component the limit belongs to (the coloured text).
   * The limit is computed with ITS filters, so both numbers compare over the
   * same rows.
   */
  columnValue(key: string, ownerComponentId = ''): number | string | null {
    const stateKey = ownerComponentId ? `${key}${COLUMN_OWNER_SEPARATOR}${ownerComponentId}` : key;
    const state = this.columnValuesState()[stateKey];
    if (!state && parseColumnLimit(key)) queueMicrotask(() => {
      if (!this.columnValuesState()[stateKey]) this.connectColumnValue(stateKey);
    });
    return state?.status === 'ready' ? state.value : null;
  }

  /** `key`: a `col:` limit, optionally `<limit><COLUMN_OWNER_SEPARATOR><owner componentId>`. */
  private connectColumnValue(key: string, refresh?: QuietRefresh): void {
    const [limitKey, owner = ''] = key.split(COLUMN_OWNER_SEPARATOR);
    const limit = parseColumnLimit(limitKey);
    if (!limit || !this.layout) return;
    // Filters still filling: this limit loads once they are set (see endCascade).
    if (this.heldForCascade()) {
      this.heldColumnKeys.add(key);
      return;
    }
    const componentId = `CMP-KPI-LIMIT-${limit.dataset}-${limit.field}-${limit.aggregation}`.replace(/[^A-Za-z0-9_-]/g, '_');
    const binding = { dataset: limit.dataset, field: limit.field, aggregation: limit.aggregation } as TextDataBinding;
    const payload = this.withCanvasFilters(owner || componentId, withFilters(textComputePayload(componentId, binding, `${limit.field} limit`),
      computeFilters(this.widgetFilters(owner || componentId), [limit.dataset])), [limit.dataset]);
    // A value already on screen stays until the new one lands - on every
    // reload, not only a quiet one: the colour it drives never blinks off.
    const shown = this.columnValuesState()[key]?.status === 'ready';
    if (!shown) this.columnValuesState.update((all) => ({ ...all, [key]: { status: 'loading' } }));
    const seq = (this.columnValueSeq.get(key) ?? 0) + 1;
    this.columnValueSeq.set(key, seq);
    this.http.post<Record<string, unknown>>(this.generateUrl(), payload)
      .pipe(catchError(() => of(null)), takeUntil(this.resetRequests))
      .subscribe((response) => {
        if (this.columnValueSeq.get(key) !== seq) return;
        const state: TextValueState = response
          ? textValueFromResult(binding, computeEntryFor(response, componentId))
          : { status: 'error', message: 'Could not load this column.' };
        if (state.status === 'error' && shown) return;
        this.columnValuesState.update((all) => ({ ...all, [key]: state }));
      });
  }

  /** Re-fetch every column value in use (filters changed, or a dashboard refresh). */
  private refreshColumnValues(refresh?: QuietRefresh): void {
    Object.keys(this.columnValuesState()).forEach((key) => this.connectColumnValue(key, refresh));
  }

  private patchInputData(nodeId: string, state: TextValueState): void {
    this.inputDataValuesState.update((all) => ({ ...all, [nodeId]: state }));
  }

  /** Fetch generated zone sliders that have no values yet (open / publish). */
  private hydrateZoneSliders(): void {
    if (!this.layout) return;
    this.zoneSliderComponents().forEach(({ node, componentId }) => {
      if (this.zoneValuesState()[node.id]) return;
      this.connectZoneSlider(node.id, componentId, zoneSliderBindingOf(node.widget?.binding), node.name ?? '');
    });
  }

  /** Zone sliders that went through Generate (they carry a componentId). */
  private zoneSliderComponents(): Array<{ node: ReturnType<typeof widgetNodes>[number]; componentId: string }> {
    return widgetNodes(this.doc.document())
      .filter((node) => node.widget?.layoutType === 'ZONE_SLIDER' && !!node.widget.componentId)
      .map((node) => ({ node, componentId: node.widget!.componentId! }));
  }

  private patchZoneValue(nodeId: string, state: ZoneSliderValueState): void {
    this.zoneValuesState.update((all) => ({ ...all, [nodeId]: state }));
  }

  /**
   * Fetch whatever a widget needs but was not published with.
   *
   * Called whenever a prerequisite lands — the document, the layout (which
   * carries the endpoints) or the applier — because any of the three can be
   * last. Each step skips widgets that already have what it fetches, so the
   * repeat calls cost nothing and the builder canvas is untouched.
   */
  hydrate(): void {
    this.hydrateDropdowns();
    // Filters still filling (e.g. from the opening screen): the rest loads once, with the last one.
    if (this.heldForCascade()) return;
    this.hydrateCharts();
    this.hydrateTexts();
    this.hydrateZoneSliders();
    this.hydrateInputData();
  }

  /** Compute every registered text, the same way charts hydrate on open. */
  hydrateTexts(): void {
    if (!this.layout) return;
    boundTextComponents(this.doc.document()).forEach((node, componentId) => {
      // `connectText` marks it loading straight away, so this also guards the
      // repeat calls hydrate() makes as each prerequisite lands.
      if (!node.dataBinding || this.textValuesState()[componentId]) return;
      this.connectText(componentId, node.dataBinding, node.name ?? '');
    });
  }

  /** Live render data for a node, or null to render the stored widget data. */
  dataFor(nodeId: string): Record<string, unknown> | null {
    const tile = this.live().get(nodeId);
    return tile ? tileRenderData(tile) : null;
  }

  tileFor(nodeId: string): DashboardTile | undefined {
    return this.tiles().find((tile) => tile.id === nodeId);
  }

  /** Show a tile live without saving it (the build form's draft preview). */
  preview(tile: DashboardTile): void {
    this.live.set(new Map(this.live()).set(tile.id, tile));
  }

  /** A widget was regenerated: drop its live state so the new build shows. */
  forget(nodeId: string): void {
    this.refreshedTiles.delete(nodeId);
    if (!this.live().has(nodeId)) return;
    const next = new Map(this.live());
    next.delete(nodeId);
    this.live.set(next);
  }

  reset(): void {
    this.resetRequests.next();
    this.resetVersion++;
    this.firstValueRequestVersions.clear();
    this.activeCascade = 0;
    this.loadAfterCascade = false;
    this.cascadeTargets.clear();
    this.cascadeAnswered.clear();
    clearTimeout(this.cascadeTimer);
    this.dashboardRefreshVersion++;
    this.tileRefreshVersions.clear();
    this.dashboardError.set('');
    this.hydrating.clear();
    this.dataOptions.clear();
    this.textValuesState.set({});
    this.zoneValuesState.set({});
    this.inputDataValuesState.set({});
    this.columnValuesState.set({});
    this.columnValueSeq.clear();
    this.heldColumnKeys.clear();
    this.inputDataSeq.clear();
    this.localDropdownState.set({});
    this.crossFilter = null;
    this.dropdownFilters.clear();
    this.activeFilters.set([]);
    this.waterfallSeq.clear();
    this.componentStamps.clear();
    this.anyComponentStamp = 0;
    this.pendingRefreshOwners.clear();
    this.refreshedTiles.clear();
    this.live.set(new Map());
    this.busy.set(new Set());
    this.pendingState.set(new Set());
    this.noDataState.set(new Set());
    this.uploadMessages.set({});
    this.buttonStatesState.set({});
  }

  exportDataFor(nodeId: string): Record<string, unknown> | null {
    const tile = this.tileFor(nodeId);
    return tile ? tileRenderData(tile) : null;
  }

  /**
   * Re-read the dashboard from the database: every chart and table (including
   * those that already show rows), every bound text and zone slider, keeping
   * the active filters. `clearFilters` resets every selection first (Reset).
   *
   * `quiet` - entering Preview, auto-refresh: what is on screen stays up until
   * its new data lands. No "Loading data…" over a widget that already shows
   * data, no spinner over a value, no error banner when a request fails (the
   * next refresh tries again). A quiet refresh asked for before the layout is
   * known runs as soon as it is.
   */
  refreshDashboard(clearFilters = false, options: { quiet?: boolean; only?: ReadonlySet<string>; componentsOnly?: boolean } = {}): void {
    const quiet = options.quiet === true;
    if (!this.layout || (quiet && !this.applier)) {
      if (quiet) this.quietRefreshWaiting = true;
      else this.dashboardError.set('Dashboard data configuration is not available yet. Try again when the dashboard has loaded.');
      return;
    }
    if (clearFilters) {
      const hadNavigation = !!this.navigationFilter;
      this.navigationFilter = null;
      this.pendingNavigationFirstValues = [];
      this.navigationDropdowns = new Map();
      this.navigationOverridden = new Set();
      if (hadNavigation) this.navigationReset.update(value => value + 1);
      this.reset();
      // Back to each dropdown's configured "Default value" (none → cleared).
      this.commit(this.tiles().filter((tile) => !!tile.dropdownControl).map((tile) => {
        const raw = tile.payload['defaultValue'];
        const value = Array.isArray(raw) ? raw.map(String) : raw == null || raw === '' ? [] : [String(raw)];
        return { ...tile, payload: { ...tile.payload, value }, dropdownControl: { ...tile.dropdownControl!, value } };
      }));
    }
    this.quietRefreshWaiting = false;
    if (this.heldForCascade()) {
      this.hydrateDropdowns();
      return;
    }
    const tiles = this.tiles();
    const version = ++this.dashboardRefreshVersion;
    const refresh: QuietRefresh = { quiet, stamp: ++this.requestClock };
    this.dashboardError.set('');
    // `only`: just these charts / texts (the ones a filter cascade changed).
    const only = options.only;
    this.refreshTexts(refresh, only);
    // `componentsOnly`: charts / texts only, the rest is up to date.
    if (!options.componentsOnly) {
      this.refreshZoneSliders(refresh);
      this.refreshInputData(refresh);
      this.refreshColumnValues(refresh);
      this.hydrateDropdowns();
    }
    tiles.filter((tile) => isChartTile(tile) && Object.keys(tile.payload ?? {}).length
      && (!only || only.has(tile.componentId))).forEach((tile) => {
      // A quiet refresh leaves a widget that already shows data on screen.
      if (!(quiet && hasRows(tileRenderData(tile)))) {
        this.setPending(tile.id, true);
        this.pendingRefreshOwners.set(tile.id, version);
      }
      // The newest refresh OF THIS CHART answers for it. A dashboard-wide number
      // let a later refresh of other charts (a filter's) drop this chart's answer
      // when the two overlapped - on a slow server, the chart then never loaded.
      this.tileRefreshVersions.set(tile.id, version);
      const latest = () => this.tileRefreshVersions.get(tile.id) === version;
      const basePayload = this.navigationPayload(tile);
      const filters = computeFilters(this.filtersFor(tile.componentId), this.datasetsForFilters(tile));
      const payload = { ...basePayload, ...(filters.length ? { filters: [...((basePayload['filters'] as unknown[]) ?? []), ...filters] } : {}),
        otherInputs: this.otherInputsFor(tiles, tile.componentId) };
      this.trackRefresh(this.widgetApi.resolve(this.generateUrl(), payload, tile.layoutType)).pipe(takeUntil(this.resetRequests)).subscribe({
        next: (response) => {
          this.releasePending(tile.id, version);
          // A newer refresh, or an interaction since (e.g. a dropdown's first value), answers instead.
          if (!latest() || this.supersededSince(tile.componentId, refresh.stamp)) return;
          this.applyResults(computeResults(response, tile), [tile.componentId], undefined, { refresh: true, answerFor: tile.id });
        },
        error: () => {
          this.releasePending(tile.id, version);
          if (!latest() || quiet) return;
          this.dashboardError.set('Some dashboard data could not be refreshed. Try again.');
        },
      });
    });
  }

  /**
   * Leaving Preview: widgets whose live data came only from a refresh go back
   * to the design's own data, so the editing canvas shows - and every edit acts
   * on - the configuration being edited (a refreshed copy holds the
   * configuration it was fetched with). State from interactions, such as a
   * picked filter, stays as before. Entering Preview reads the database again.
   */
  dropRefreshedData(): void {
    if (!this.refreshedTiles.size) return;
    const next = new Map(this.live());
    this.refreshedTiles.forEach((id) => next.delete(id));
    this.refreshedTiles.clear();
    this.live.set(next);
  }

  /** Auto-refresh tick: skipped (not queued) while hidden, not ready, or still busy with the last one. */
  private autoRefreshTick(): void {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (!this.layout || !this.applier || this.refreshesInFlight > 0) return;
    // Mid-interaction (a filter recomputing) or a first load still running: next tick.
    if (this.busy().size || this.pendingState().size) return;
    this.refreshDashboard(false, { quiet: true });
  }

  // ── Input controls ─────────────────────────────────────────────────────────

  onSliderChange(nodeId: string, range: { start: number; end: number }): void {
    this.onInputValueChange(nodeId, range?.end);
  }

  /** Blank the INPUT_BOX tiles among `componentIds` (null shows the box's empty "-"). */
  private clearTargetInputs(tiles: DashboardTile[], componentIds: ReadonlySet<string>): void {
    const cleared = tiles
      .filter((target) => target.layoutType === 'INPUT_BOX' && target.inputControl && componentIds.has(target.componentId))
      .map((target) => ({
        ...target,
        payload: { ...target.payload, value: null },
        inputControl: { ...target.inputControl!, value: null, widgetData: { ...(target.inputControl!.widgetData ?? {}), value: null } },
      }));
    if (!cleared.length) return;
    this.inputValuesState.update((all) => {
      const next = { ...all };
      cleared.forEach((target) => delete next[target.id]);
      return next;
    });
    this.commit(cleared);
  }

  onInputValueChange(nodeId: string, value: number | string): void {
    this.inputValuesState.update((all) => ({ ...all, [nodeId]: value }));
    const tile = this.tileFor(nodeId);
    if (!tile?.inputControl) return;
    const tiles = this.tiles();
    const payload: Record<string, unknown> = { ...tile.payload, value, otherInputs: this.otherInputsFor(tiles, tile.componentId) };
    this.commit([{ ...tile, payload, inputControl: { ...tile.inputControl, value } }]);
    const targetIds = targetIdsOf(payload['targets']);
    const sideTiles = sideEffectInputsFor(tile, tiles);
    // No targets and no custom API: the value only feeds formulas / buttons - nothing to compute.
    if (!targetIds.length && !sideTiles.length && !tile.inputControl.apiConfig?.enabled) return;
    const stamp = this.beginRecompute([...targetIds, ...sideTiles.map((side) => side.componentId)]);

    const runMain = () => this.ensureSaved(registeredIdsFor(payload, targetIds))
      .pipe(switchMap(() => this.inputCompute.resolve(tile.inputControl?.apiConfig, this.generateUrl(), this.withTargetCanvasFilters(payload, targetIds), { value, componentId: tile.componentId })), takeUntil(this.resetRequests))
      .subscribe({ next: (res) => this.applyResults(res, targetIds, undefined, { stamp }), error: () => this.markBusy([]) });
    if (!sideTiles.length) { runMain(); return; }

    // "SSA $" side-effect inputs recompute first, then the driving input.
    forkJoin(sideTiles.map((side) => this.http
      .post<Record<string, unknown>>(this.generateUrl(), this.withTargetCanvasFilters({ ...side.payload, otherInputs: this.otherInputsFor(tiles, side.componentId) },
        targetIdsOf(side.payload?.['targets'])))
      .pipe(map((response) => ({ side, response })), catchError(() => of({ side, response: null })))))
      .pipe(takeUntil(this.resetRequests))
      .subscribe((results) => {
        results.forEach(({ side, response }) => response && this.applyResults(response, targetIdsOf(side.payload?.['targets']), undefined, { stamp }));
        runMain();
      });
  }

  /** `tileOverride` / `basePayload`: the dropdown step pushes a freshly generated tile. */
  onDropdownChange(nodeId: string, value: string | string[], basePayload?: Record<string, unknown>, tileOverride?: DashboardTile, firstValueChain: ReadonlySet<string> = new Set()): void {
    // A new pick starts a new cascade; one still filling is superseded (what it
    // held back loads when this one ends).
    this.activeCascade = 0;
    this.changeDropdown(nodeId, value, { basePayload, tileOverride, chain: firstValueChain, cascade: ++this.cascadeToken });
  }

  /**
   * One step of a filter cascade. While more dropdowns fill from this answer
   * ("Show first value", a spec's only customer) the request asks for those
   * dropdowns only; charts, texts and sliders load ONCE, with the last filter
   * (`final`), instead of after every step.
   *
   * `alongside`: dropdowns an auto-fill already set (see `applyAutoPicks`) - they ride
   * in this one request (their values go as otherInputs, their dependents as targets).
   */
  private changeDropdown(nodeId: string, value: string | string[], options: DropdownStep): void {
    const { basePayload, tileOverride, alongside = [] } = options;
    const firstValueChain = options.chain ?? new Set<string>();
    const tile = tileOverride ?? this.tileFor(nodeId);
    const control = tile?.dropdownControl;
    if (!tile || !control) {
      // Not generated: nothing to filter, but the pick must still show.
      this.localDropdownState.update((all) => ({ ...all, [nodeId]: value }));
      return;
    }
    // "Currency exchange": the picked value is a rate its targets are shown ×,
    // not a filter - nothing is sent, the converted layers follow at once.
    if (this.currencyExchangeIds().has(tile.id)) {
      const rate = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
      this.commit([{ ...tile, payload: { ...tile.payload, value: rate }, dropdownControl: { ...control, value: rate } }]);
      return;
    }
    // "Show the value only": the pick is kept and shown - no request, no filter, no target refreshed.
    if (this.valueOnlyIds().has(tile.id)) {
      const picked = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
      this.commit([{ ...tile, payload: { ...tile.payload, value: picked }, dropdownControl: { ...control, value: picked } }]);
      if (this.dropdownFilters.delete(tile.componentId)) this.publishFilters();
      return;
    }
    const selected =Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
    this.holdDropdownValue(tile, selected);
    const field = control.field ?? '';
    this.publishFilters();

    const tiles = this.tiles();
    this.pruneDropdownFilters(tiles);
    const riders = alongside
      .map((id) => tiles.find((candidate) => candidate.id === id))
      .filter((rider): rider is DashboardTile => !!rider?.dropdownControl && rider.id !== nodeId);
    const sources = [tile, ...riders];
    // This tile is committed below; the riders already hold their new value.
    const valueOf = (source: DashboardTile): string[] => source === tile ? selected : (source.dropdownControl?.value ?? []).map(String);
    // The riders are already filled: nothing in this request resets or re-fills them.
    const chain = new Set([...firstValueChain, ...riders.map((rider) => rider.id)]);
    // A dropdown recomputes what it TARGETS (its "Target chart(s) / input(s)");
    // only one that lists no targets filters every chart, as dropdowns used to.
    // Targets come from the design (see designTargetsOf), not the live payload.
    const dropdownIds = new Set(tiles.filter((candidate) => candidate.dropdownControl).map((candidate) => candidate.componentId));
    const ownTargets = this.designTargetsOf(tile);
    const ownPayloadTargets = basePayload?.['targets'];
    if (Array.isArray(ownPayloadTargets)) ownPayloadTargets.forEach((target) => {
      const id = target && typeof target === 'object' ? String((target as Record<string, unknown>)['componentId'] ?? '') : '';
      if (id) ownTargets.add(id);
    });
    const targets = ownTargets.size ? [] : chartTargets(this.doc.document(), tile.componentId, field);
    // Texts / values, and charts NO dropdown targets, follow every dropdown, as
    // they always did: they refresh, with every filter, once this request is
    // answered (endCascade) - not inside it, which would tie them to this dropdown alone.
    if (ownTargets.size) {
      const targeted = this.explicitlyTargeted();
      const charts = this.targetedChartIds();
      chartTargets(this.doc.document(), tile.componentId, field)
        .filter((target) => !(charts.has(target.componentId) && targeted.has(target.componentId)))
        .forEach((target) => this.cascadeTargets.add(target.componentId));
    }
    const firstValueIds = new Set(sources.flatMap((source) => [...this.firstValueDropdownIds(source)]));
    const firstValueVersion = (this.firstValueRequestVersions.get(nodeId) ?? 0) + 1;
    if (firstValueIds.size) this.firstValueRequestVersions.set(nodeId, firstValueVersion);
    tiles.forEach((target) => {
      if (target.id !== nodeId && target.dropdownControl && firstValueIds.has(target.id) && !chain.has(target.id)) {
        targets.push({ componentId: target.componentId, label: target.title, field });
      }
    });
    // Each dropdown's configured targets go too: its dependent dropdowns (their
    // options), and - for THIS dropdown - its charts / texts. The backend filters
    // every target of a request by its source's value, so another dropdown's
    // charts never ride along: they refresh, with their own filters, when the
    // cascade ends (endCascade).
    const sent = new Set(targets.map((target) => target.componentId));
    sources.forEach((source) => {
      [...(source === tile ? ownTargets : this.designTargetsOf(source))].forEach((componentId) => {
        if (!componentId || componentId === source.componentId || componentId.startsWith(TEXT_TARGET_PREFIX) || sent.has(componentId)) return;
        if (source !== tile && !dropdownIds.has(componentId)) {
          this.cascadeTargets.add(componentId);
          return;
        }
        sent.add(componentId);
        targets.push({ componentId, label: tiles.find((candidate) => candidate.componentId === componentId)?.title ?? '', field });
      });
      // Deselected (value cleared): the dropdown itself is a target too, so the
      // answer refreshes its own options along with everything it filters.
      if (!valueOf(source).length && source.componentId && !sent.has(source.componentId)) {
        sent.add(source.componentId);
        targets.push({ componentId: source.componentId, label: source.title, field });
      }
    });
    // Previous dependent selections must not constrain the new option lists
    // before those dropdowns receive their first value for this customer.
    const resettingComponents = new Set(tiles.filter((target) => firstValueIds.has(target.id)
      && !chain.has(target.id)).map((target) => target.componentId));
    // More dropdowns fill from this answer: ask for the dropdowns only - the
    // charts would be recomputed again with the next filter anyway.
    const navigationDriven = selected.length > 0 && this.navigationDropdowns.has(tile.id);
    const filling = !options.final && (resettingComponents.size > 0 || navigationDriven);
    if (filling) {
      // Its charts / texts wait for the cascade's end.
      targets.filter((target) => !dropdownIds.has(target.componentId)).forEach((target) => this.cascadeTargets.add(target.componentId));
      targets.splice(0, targets.length, ...targets.filter((target) => dropdownIds.has(target.componentId)));
      if (options.cascade === this.cascadeToken) {
        this.activeCascade = options.cascade;
        this.armCascadeTimeout(options.cascade);
      }
    } else {
      targets.forEach((target) => this.cascadeAnswered.add(target.componentId));
    }
    const otherInputs = this.otherInputsFor(tiles, tile.componentId).map((input) =>
      resettingComponents.has(input.componentId) ? { ...input, value: [] } : input);
    const ownPayload = basePayload ?? tile.payload;
    const payload = { ...ownPayload, value: selected, targets, otherInputs };
    this.commit([{ ...tile, payload, dropdownControl: { ...control, value: selected } }]);
    // Cleared: its exchange-rate dropdowns clear at once (values back to as stored).
    if (!selected.length) this.selectExchangeRates(tile, new Set(), true);
    // Cleared: the input boxes it fills clear too. An empty answer is skipped
    // for inputs (see applyResults), so they would keep the last value shown.
    if (!selected.length) this.clearTargetInputs(tiles, ownTargets);

    // No early return on an empty target set: a filter change always calls the
    // API. The backend answers for the dropdown's own component too, and
    // `applyResults` matches results by componentId when no targets were sent.
    const targetIds = targets.map((target) => target.componentId).filter(Boolean);
    const stamp = this.beginRecompute(targetIds);
    this.ensureSaved(registeredIdsFor(payload, targetIds))
      .pipe(switchMap(() => this.inputCompute.resolve(control.apiConfig, this.generateUrl(), this.withTargetCanvasFilters(payload, targetIds), { ...payload, value: selected, componentId: tile.componentId, field })), takeUntil(this.resetRequests))
      .subscribe({ next: (res) => {
        if (firstValueIds.size && this.firstValueRequestVersions.get(nodeId) !== firstValueVersion) {
          console.warn(`[design] Dropdown "${tile.title}": a newer request replaced this answer; it is not applied.`);
          return;
        }
        try {
        const optionIds = navigationDriven ? new Set([...firstValueIds, ...this.dropdownTargetIds(tile)]) : firstValueIds;
        const refreshed = this.applyResults(res, targetIds, optionIds, { stamp });
        const nextChain = new Set([...chain, nodeId]);
        const picks: AutoPick[] = [];
        sources.forEach((source) => {
          const current = this.tileFor(source.id) ?? source;
          picks.push(...this.firstValuePicks(current, nextChain, refreshed, !valueOf(source).length));
          if (valueOf(source).length) this.selectExchangeRates(current, refreshed, false);
        });
        // Opened from another screen: what this answer fills is settled - the
        // only-option pick (a spec's customer) does not refill it.
        const settled = navigationDriven ? new Set([...nextChain, ...firstValueIds]) : nextChain;
        if (navigationDriven) picks.push(...this.onlyOptionPicks(tile, refreshed, settled));
        // A dropdown with typed options this answer left empty starts on its first data value.
        const typedPicks = this.typedOptionPicks(refreshed, settled);
        if (!filling) {
          this.endCascade(options.cascade);
          if (typedPicks.length) this.applyAutoPicks(typedPicks, settled);
          return;
        }
        picks.unshift(...typedPicks);
        if (this.applyAutoPicks(picks, settled, options.cascade)) return;
        // Nothing more to fill: every filter is set - now the charts, once.
        this.changeDropdown(nodeId, selected, { basePayload: ownPayload, chain: new Set([...settled, ...firstValueIds]),
          alongside: riders.map((rider) => rider.id), cascade: options.cascade, final: true });
        } catch (error) {
          // A failure here must not leave the screen waiting on this cascade.
          console.error(`[design] Dropdown "${tile.title}": its answer could not be applied.`, error);
          this.markBusy([]);
          this.endCascade(options.cascade);
        }
      }, error: () => {
        this.markBusy([]);
        this.endCascade(options.cascade);
      } });
  }

  /**
   * A cascade finished (or was superseded): what asked to load while it was
   * filling loads now - charts the last answer did not fill, texts, sliders.
   */
  private endCascade(cascade: number): void {
    if (this.activeCascade && this.activeCascade !== cascade) return;
    this.activeCascade = 0;
    clearTimeout(this.cascadeTimer);
    // Charts / texts a dropdown of the cascade targets that no request of it
    // answered (each request carries its OWN dropdown's targets only), plus
    // whatever never loaded: refreshed once, each with its own filters.
    const unanswered = [...this.cascadeTargets].filter((id) => !this.cascadeAnswered.has(id));
    this.cascadeTargets.clear();
    this.cascadeAnswered.clear();
    if (!this.loadAfterCascade && !unanswered.length) return;
    // Nothing was held back (a plain dropdown change): just those charts / texts -
    // its sliders and inputs already reloaded with the filter change.
    if (!this.loadAfterCascade) {
      this.refreshDashboard(false, { only: new Set(unanswered), componentsOnly: true });
      return;
    }
    this.loadAfterCascade = false;
    const textValues = this.textValuesState();
    const neverLoaded = [
      ...this.tiles().filter((tile) => isChartTile(tile) && !hasRows(tileRenderData(tile))).map((tile) => tile.componentId),
      ...[...boundTextComponents(this.doc.document()).keys()].filter((componentId) => !textValues[componentId]),
    ];
    const held = new Set(this.heldColumnKeys);
    this.heldColumnKeys.clear();
    this.refreshDashboard(false, { only: new Set([...unanswered, ...neverLoaded]) });
    held.forEach((key) => this.connectColumnValue(key));
  }

  /** True while a cascade is filling: the caller holds its load back, and it runs at the cascade's end. */
  private heldForCascade(): boolean {
    if (!this.activeCascade) return false;
    this.loadAfterCascade = true;
    return true;
  }

  /** The local half of a dropdown change: navigation hand-over and its filter entry. */
  private holdDropdownValue(tile: DashboardTile, selected: string[]): void {
    const fromNavigation = this.navigationDropdowns.get(tile.id);
    if (fromNavigation && (selected.length !== fromNavigation.values.length || selected.some((item, i) => item !== fromNavigation.values[i]))) {
      this.navigationOverridden.add(fromNavigation.key);
      this.navigationDropdowns.delete(tile.id);
    }
    const field = tile.dropdownControl?.field ?? '';
    if (selected.length && field) this.dropdownFilters.set(tile.componentId, { field, values: selected, textTargets: textTargetsOf(tile) });
    else this.dropdownFilters.delete(tile.componentId);
  }

  /**
   * Values an auto-fill (Show first value / the only option) chose, applied
   * together: all are set at once and ONE compute follows, carrying them all,
   * instead of one whole-dashboard recompute per dropdown racing the others.
   * A pick that another pick fills in turn waits for that one's answer - its
   * first value depends on it - unless `chain` marks it settled.
   */
  private applyAutoPicks(picks: AutoPick[], chain: ReadonlySet<string>, cascade = ++this.cascadeToken): boolean {
    const unique = [...new Map(picks.map((pick) => [pick.id, pick])).values()];
    if (!unique.length) return false;
    // A currency-exchange / value-only dropdown sends nothing: set it straight away.
    const exchangeIds = this.localOnlyIds();
    unique.filter((pick) => exchangeIds.has(pick.id)).forEach((pick) => this.changeDropdown(pick.id, pick.values, { chain, cascade }));
    const sendable = unique.filter((pick) => !exchangeIds.has(pick.id));
    if (!sendable.length) return false;
    const dependents = new Set(sendable.flatMap((pick) => {
      const tile = this.tileFor(pick.id);
      return tile ? [...this.firstValueDropdownIds(tile)].filter((id) => !chain.has(id)) : [];
    }));
    const independent = sendable.filter((pick) => !dependents.has(pick.id));
    const batch = independent.length ? independent : sendable.slice(0, 1);
    const last = batch[batch.length - 1];
    const riders = batch.slice(0, -1).filter((pick) => {
      const tile = this.tileFor(pick.id);
      if (!tile?.dropdownControl) return false;
      this.holdDropdownValue(tile, pick.values);
      this.commit([{ ...tile, payload: { ...tile.payload, value: pick.values }, dropdownControl: { ...tile.dropdownControl, value: pick.values } }]);
      if (!pick.values.length) this.selectExchangeRates(tile, new Set(), true);
      return true;
    });
    this.changeDropdown(last.id, last.values, { chain, alongside: riders.map((pick) => pick.id), cascade });
    return true;
  }

  /**
   * A "Currency exchange" dropdown refreshed by its source (View in → GBP): it
   * now lists that currency's rate, so the first one is selected - unless the
   * rate already picked is still listed. Cleared when nothing is listed, or
   * when the source itself was cleared.
   */
  private selectExchangeRates(source: DashboardTile, refreshed: ReadonlySet<string>, sourceCleared: boolean): void {
    const exchangeIds = this.currencyExchangeIds();
    const targets = dropdownTargetsOf(source);
    // The source (View in) was cleared: its exchange dropdowns are cleared too -
    // values go back to as stored - instead of picking the first of all rates.
    if (sourceCleared) {
      this.tiles().filter((tile) => exchangeIds.has(tile.id) && targets.has(tile.componentId) && tile.dropdownControl?.value?.length)
        .forEach((tile) => this.onDropdownChange(tile.id, []));
      return;
    }
    for (const id of refreshed) {
      if (!exchangeIds.has(id)) continue;
      const control = this.tileFor(id)?.dropdownControl;
      if (!control) continue;
      const options = (control.options ?? []).map(String);
      const current = control.value?.[0];
      if (current !== undefined && options.includes(String(current))) continue;
      const tile = this.tileFor(id);
      const pickable = tile ? this.pickableOptions(tile) : [];
      this.onDropdownChange(id, pickable.length ? [pickable[0]] : []);
    }
  }

  /** Opt-in only: select the first ordered option of the dropdowns chosen by this filter. */
  private firstValueDropdownIds(source: DashboardTile): Set<string> {
    if (source.payload['showFirstValue'] !== true) return new Set();
    const configured = source.payload['firstValueDropdownIds'];
    if (Array.isArray(configured) && configured.length) {
      return new Set(configured.filter((id): id is string => typeof id === 'string'));
    }
    // The picker is optional: without an explicit list use the source's
    // existing dropdown targets, which are stored as component IDs.
    const targets = new Set(source.dropdownControl?.targets ?? []);
    const payloadTargets = source.payload['targets'];
    if (Array.isArray(payloadTargets)) payloadTargets.forEach((target) => {
      if (target && typeof target === 'object') targets.add(String(target['componentId'] ?? ''));
    });
    return new Set(this.tiles().filter((tile) => tile.id !== source.id && tile.dropdownControl
      && (targets.has(tile.componentId) || targets.has(tile.id))).map((tile) => tile.id));
  }

  /**
   * A value sent by the opening screen (e.g. a spec) narrows an empty target
   * dropdown to ONE option (that spec's customer): select it, without any
   * "Show first value" setting - there is nothing to choose. `chain` keeps the
   * selected dropdown from re-filling the ones the source already fills.
   */
  private onlyOptionPicks(source: DashboardTile, refreshed: ReadonlySet<string>, chain: ReadonlySet<string>): AutoPick[] {
    const targets = this.dropdownTargetIds(source);
    const picks: AutoPick[] = [];
    for (const target of this.tiles()) {
      const control = target.dropdownControl;
      if (!control || !targets.has(target.id) || chain.has(target.id) || this.navigationDropdowns.has(target.id)) continue;
      // Typed (static) options are always listed, so they don't count here.
      const pickable = this.pickableOptions(target);
      if (!refreshed.has(target.id) || (control.value ?? []).length || pickable.length !== 1) continue;
      picks.push({ id: target.id, values: [pickable[0]] });
    }
    return picks;
  }

  /**
   * A dropdown with typed (static) options that has nothing selected starts on
   * its first value from the data (typed ones are only offered) - when its
   * options load on open and whenever a refresh leaves it empty. Not one the
   * opening screen sent a value for, nor one this cascade already set.
   */
  private typedOptionPicks(refreshed: ReadonlySet<string>, chain: ReadonlySet<string>): AutoPick[] {
    const picks: AutoPick[] = [];
    for (const id of refreshed) {
      const tile = this.tileFor(id);
      const control = tile?.dropdownControl;
      if (!tile || !control || chain.has(id) || this.navigationDropdowns.has(id)) continue;
      if ((control.value ?? []).length || !staticDropdownOptions(tile.payload).length) continue;
      const first = this.pickableOptions(tile)[0];
      if (first !== undefined) picks.push({ id, values: [first] });
    }
    return picks;
  }

  /**
   * The options an automatic pick (first value, only option) chooses from, in
   * the order shown: the values the data answered with - a typed option counts
   * only when the data has it too (e.g. a record's "Strategic account").
   * Before any data answer, the listed options minus the typed ones.
   */
  private pickableOptions(tile: DashboardTile): string[] {
    const options = (tile.dropdownControl?.options ?? []).map(String);
    const data = this.dataOptions.get(tile.id);
    return data ? options.filter((option) => data.includes(option)) : pickableDropdownOptions(options, tile.payload);
  }

  /** Ids of the dropdowns `source` lists as its targets. */
  private dropdownTargetIds(source: DashboardTile): Set<string> {
    const targets = new Set(source.dropdownControl?.targets ?? []);
    return new Set(this.tiles().filter((tile) => tile.id !== source.id && tile.dropdownControl
      && (targets.has(tile.componentId) || targets.has(tile.id))).map((tile) => tile.id));
  }

  /** `sourceCleared`: the user just cleared `source` - its dropdowns are cleared, not filled. */
  private firstValuePicks(source: DashboardTile, chain: ReadonlySet<string>, refreshed?: ReadonlySet<string>, sourceCleared = false): AutoPick[] {
    const picks: AutoPick[] = [];
    for (const id of this.firstValueDropdownIds(source)) {
      // A value the opening screen sent is kept until the user changes it.
      if (chain.has(id) || this.navigationDropdowns.has(id)) continue;
      const target = this.tileFor(id);
      const control = target?.dropdownControl;
      if (!target || !control) continue;
      // The source was cleared: clear the dropdowns it auto-fills instead of
      // filling them with the first value of the now-unfiltered list.
      if (sourceCleared) {
        if ((control.value ?? []).length || this.dropdownFilters.has(target.componentId)) picks.push({ id, values: [] });
        continue;
      }
      if (refreshed && !refreshed.has(id)) continue;
      const data = control.widgetData ?? {};
      const options = orderDropdownOptions(this.pickableOptions(target),
        normalizeDropdownOptionOrderMode(data['optionOrderMode']), String(data['customOptionOrder'] ?? ''));
      // The first value from the data - a typed (static) option is never auto-selected.
      const first = options[0];
      if (first === undefined) {
        if (this.dropdownFilters.has(target.componentId)) picks.push({ id, values: [] });
        continue;
      }
      if ((control.value ?? []).length === 1 && control.value![0] === first && this.dropdownFilters.has(target.componentId)) continue;
      picks.push({ id, values: [String(first)] });
    }
    return picks;
  }

  onFilePicked(nodeId: string, file: File): void {
    const resetVersion = this.resetVersion;
    const tile = this.tileFor(nodeId);
    const control = tile?.fileUploadControl;
    if (!tile || !control || !file) return;
    const targetIds = (control.targets ?? []).filter(Boolean);
    this.setUploadMessage(nodeId, `Uploading ${file.name}…`);
    const stamp = this.beginRecompute(targetIds);
    const labelOf = (id: string) => this.tiles().find((item) => item.componentId === id)?.title || id;
    fileToBase64(file).then((base64) => {
      if (resetVersion !== this.resetVersion) return;
      const payload = {
        componentId: tile.componentId,
        dashboardId: tile.payload?.['dashboardId'] ?? null,
        componentType: 'INPUT',
        inputType: 'FILE_UPLOAD',
        fileName: file.name,
        fileType: file.type,
        fileContent: base64,
        value: base64,
        defaultValue: base64,
        targets: targetIds.map((componentId) => ({ componentId, label: labelOf(componentId) })),
      };
      const url = resolveSelfServiceApiUrl(this.layout?.apiEndpoints?.generateConfigApi, '/components/compute');
      this.ensureSaved(targetIds)
        .pipe(switchMap(() => this.inputCompute.resolve(control.apiConfig, url, this.withTargetCanvasFilters(payload, targetIds), { value: base64, componentId: tile.componentId, fileName: file.name, fileType: file.type })), takeUntil(this.resetRequests))
        .subscribe({
          next: (res) => { this.applyResults(res, targetIds, undefined, { stamp }); this.setUploadMessage(nodeId, `Uploaded ${file.name}`); },
          error: () => { this.markBusy([]); this.setUploadMessage(nodeId, `Upload failed for ${file.name}`); },
        });
    }).catch(() => { if (resetVersion !== this.resetVersion) return; this.markBusy([]); this.setUploadMessage(nodeId, `Could not read ${file.name}`); });
  }

  /**
   * Fetch one dropdown's option list again (its option filter / settings were
   * regenerated) - through the same options request as on open, so its targets
   * are not recomputed. Its selected value stays.
   */
  reloadDropdownOptions(nodeId: string): void {
    this.hydrating.delete(nodeId);
    this.dataOptions.delete(nodeId);
    const live = this.live().get(nodeId);
    if (live?.dropdownControl?.options?.length) {
      this.live.set(new Map(this.live()).set(nodeId, { ...live, dropdownControl: { ...live.dropdownControl, options: [] } }));
    }
    this.hydrateDropdowns();
  }

  /**
   * Load each dropdown's options from the compute API (`/components/compute`),
   * never from options saved with the design (see `tiles()`, which drops them)
   * nor from a raw distinct-values query on the dataset.
   *
   * The compute API answers for a request's TARGETS, not for its source, and it
   * stores the source's targets. So the request comes from a separate
   * "options" source (`<componentId>-options`) that targets the dropdown: the
   * answer is the dropdown's distinct values (narrowed by the other dropdowns'
   * picks), and nothing is written onto the real dropdown - targeting itself
   * would make its own picks narrow its own list later. No `dashboardId` is
   * sent: with one, the backend clears the dashboard's live computed values.
   * Safe to call more than once: a dropdown with live options, or already
   * asked, is skipped.
   */
  hydrateDropdowns(): void {
    if (!this.layout) return;
    this.tiles().forEach((tile) => {
      const control = tile.dropdownControl;
      // Options already loaded this session are live; saved ones never reach here.
      if (tile.layoutType !== 'DROPDOWN' || !control || control.options?.length) return;
      const dataset = tileDataset(tile);
      if (!control.field || !dataset || !tile.componentId || this.hydrating.has(tile.id)) return;
      this.hydrating.add(tile.id);
      this.setPending(tile.id, true);
      const field = control.field;
      const body = {
        componentId: `${tile.componentId}-options`,
        inputType: 'DROPDOWN',
        dataset,
        field,
        value: [],
        targets: [{ componentId: tile.componentId, label: tile.title, field }],
        otherInputs: this.otherInputsFor(this.tiles(), tile.componentId),
      };
      // The dropdown must be in the component store for the compute API to answer for it.
      this.ensureSaved([tile.componentId])
        .pipe(
          switchMap(() => this.http.post<Record<string, unknown>>(this.generateUrl(), this.withTargetCanvasFilters(body, [tile.componentId]))),
          catchError(() => of(null)),
          takeUntil(this.resetRequests),
        )
        .subscribe((response) => {
          this.setPending(tile.id, false);
          const current = this.tileFor(tile.id);
          if (!current?.dropdownControl) return;
          const listed = computedDropdownOptions(response, tile.componentId, field);
          if (!listed.length) {
            console.warn(`[design] Dropdown "${tile.title || field}" got no options from the compute API.`, response);
            return;
          }
          this.dataOptions.set(tile.id, listed);
          const widgetData = (current.dropdownControl.widgetData ?? {}) as Record<string, unknown>;
          // The typed (static) options are listed with the data's values.
          const options = orderDropdownOptions(
            withStaticOptions(staticDropdownOptions(current.payload), listed),
            normalizeDropdownOptionOrderMode(widgetData['optionOrderMode']),
            String(widgetData['customOptionOrder'] ?? ''),
          );
          this.live.set(new Map(this.live()).set(tile.id, {
            ...current,
            dropdownControl: { ...current.dropdownControl, options, value: current.dropdownControl.value ?? [] },
          }));
          // Option requests finish independently; apply this only once the
          // chosen dropdown's own list is available, regardless of load order.
          // Only for a source with nothing selected: a source WITH a value (picked,
          // defaulted or sent by navigation) fills its dropdowns from its own,
          // filtered answer - this unfiltered list picked wrong values (QSK38 for a
          // QSK60 spec) and fired extra requests with an impossible filter mix.
          // Nor while ANY filter is active (e.g. a spec sent by the opening
          // screen): the unfiltered list then offers values that don't match it
          // (Haul Truck for a Wheel Loader spec), and the filtered fill follows.
          if (this.navigationDropdowns.size || this.dropdownFilters.size) return;
          // A dropdown with typed (static) options starts on its first data value -
          // the same rule: never from this unfiltered list while the screen was
          // opened with values (they may not be applied yet); the filtered answer picks then.
          if (!this.navigationFilter && this.applyAutoPicks(this.typedOptionPicks(new Set([tile.id]), new Set()), new Set())) return;
          this.tiles().forEach((source) => {
            if (this.firstValueDropdownIds(source).has(tile.id) && !(source.dropdownControl?.value ?? []).length) {
              this.applyAutoPicks(this.firstValuePicks(source, new Set([source.id])), new Set([source.id]));
            }
          });
        });
    });
  }

  /**
   * Fill every chart from `generateConfigApi` (`/components/compute`).
   *
   * Same call the DVM published dashboard makes on open: POST the tile's own
   * `payload` and apply the result through the family steps. A published design
   * ships the definition only, so this is where its rows come from. Tiles that
   * already hold rows (the builder canvas) are skipped — a stripped widget still
   * carries its config (title, levels…), so "has render data" is not enough.
   */
  hydrateCharts(): void {
    if (!this.layout || !this.applier) return;
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/components/compute');
    const live = this.live();
    this.tiles().forEach((tile) => {
      // A nested table always asks the compute API: rows a design was saved
      // with (older saves) are a stale copy, never a reason to skip the call.
      const liveOnly = tile.layoutType === 'NESTED_TABLE' && !live.has(tile.id);
      if (!isChartTile(tile) || (!liveOnly && hasRows(tileRenderData(tile))) || this.hydrating.has(tile.id)) return;
      if (!tile.payload || !Object.keys(tile.payload).length) return;
      this.hydrating.add(tile.id);
      this.setPending(tile.id, true);
      this.widgetApi
        .resolve(url, this.navigationPayload(tile), tile.layoutType)
        .pipe(catchError(() => {
          this.dashboardError.set('Some dashboard data could not be loaded. Check the API configuration and try Refresh.');
          return of(null);
        }), takeUntil(this.resetRequests))
        .subscribe((response) => {
          this.setPending(tile.id, false);
          if (response) this.applyResults(computeResults(response, tile), [tile.componentId]);
        });
    });
  }

  // ── Cross-filter ───────────────────────────────────────────────────────────

  onCrossFilter(nodeId: string, event: CrossFilterEvent): void {
    const source = this.tileFor(nodeId);
    if (!source) return;
    const column = effectiveTileFilterKey(source) || event.column;
    const values = Array.isArray(event.values) ? event.values : event.value ? [event.value] : [];
    this.crossFilter = values.length ? { column, values, sourceTileId: source.id } : null;
    this.publishFilters();
    if (values.length) this.postCrossFilterEvent(source, column, values);
    this.applyCrossFilter();
  }

  /** A clicked table value: filter by it, or clear when the same value is clicked again. */
  toggleCrossFilter(nodeId: string, column: string, value: unknown): void {
    const text = value === null || value === undefined ? '' : String(value);
    const current = this.crossFilter;
    const same = !!current && current.column === column && current.values.length === 1 && current.values[0] === text;
    this.onCrossFilter(nodeId, { column, value: same || !text ? null : text });
  }


  // ── Navigation (table cells / popup buttons) ───────────────────────────────

  navigateToDashboard(slug: string, query?: { filterKey?: string; filterValue?: string; targetComponents?: string[] }): void {
    if (!slug) return;
    this.router.navigate(['/dvm-published', slug], {
      queryParams: query
        ? {
            filterKey: query.filterKey,
            filterValue: query.filterValue,
            ...(query.targetComponents?.length ? { filterTargets: query.targetComponents.join(',') } : {}),
          }
        : undefined,
    });
  }

  // ── Buttons ────────────────────────────────────────────────────────────────

  /**
   * A button inside a table (e.g. a nested level's "Planning View") — run like a
   * BUTTON widget from a config of the same shape. `stateId` keys its busy /
   * message state; `extraTokens` (the level's rows, its parent row) join the
   * request-body tokens.
   */
  /**
   * Excel export through the platform API (GenericExcelExportService): every row
   * of `entity` matching the dashboard's current selections - dropdowns and
   * chart cross-filters - saved as .xlsx. A selection on a column the entity
   * does not have is left out (the export would fail on it). `stateId` is the
   * button whose busy / result message shows.
   */
  exportExcel(stateId: string, options: ExcelExportRun): void {
    const entity = (options.entity ?? '').trim();
    if (!entity) {
      this.setButtonState(stateId, { message: 'Pick the dataset to export', kind: 'error' });
      return;
    }
    const selections = this.activeFilters().filter((filter) => filter.field && filter.values.length);
    const chosen = (options.filterColumns ?? []).map((column) => column.trim()).filter(Boolean);
    // Chosen columns that found no value anywhere - not sent, and named in the result message.
    let skipped: string[] = [];
    const wanted = [...new Set((options.columns ?? []).map((column) => column.trim()).filter(Boolean))];
    // The dataset's real columns - needed to check the exported columns and, with no
    // filter columns chosen, the dashboard selections (unknown columns fail the export).
    const known$: Observable<string[]> = wanted.length || !chosen.length
      ? this.datasets.getEntityAttributes(entity).pipe(catchError(() => of<string[]>([])))
      : of<string[]>([]);
    const request$: Observable<{ filters: ExcelExportFilter[]; columns: string[] }> = known$.pipe(map((known) => {
      const byName = new Map(known.map((column) => [column.toLowerCase(), column] as const));
      const real = (field: string) => (byName.size ? byName.get(field.toLowerCase()) ?? '' : field);
      // Exported columns: the table's configured ones that the dataset has, in table order.
      const columns = [...new Set(wanted.map(real).filter(Boolean))];
      if (wanted.length && columns.length < wanted.length) {
        console.warn(`[design-builder] export: not dataset columns, left out: ${wanted.filter((field) => !real(field)).join(', ')}`);
      }
      if (chosen.length) {
        // Chosen filter columns: rows above the table, its own rows, else the dashboard's selection.
        const filters = chosen.map((field) => ({
          field,
          values: excelFilterValues(field, { contextRows: options.contextRows, rows: options.rows, aliases: options.aliases, selections }),
        }));
        skipped = filters.filter((filter) => !filter.values.length).map((filter) => filter.field);
        if (skipped.length) console.warn(`[design-builder] export: no value for ${skipped.join(', ')} - not sent as a filter`);
        return { filters, columns };
      }
      const filters = selections.map((filter) => ({ field: real(filter.field), values: filter.values })).filter((filter) => !!filter.field);
      return { filters, columns };
    }));
    const fileName = (options.fileName || entity).trim();
    const sheetName = (options.sheetName || 'Sheet1').trim();
    const url = (options.url ?? '').trim() ? resolveSelfServiceApiUrl(options.url) : getSelfServicePlatformApiUrl();
    this.setButtonState(stateId, { busy: true, message: '', kind: '' });
    request$.pipe(
      switchMap(({ filters, columns }) => {
        const template = (options.bodyTemplate ?? '').trim();
        const userEmail = this.currentUserEmail();
        const body = template
          ? withExportColumns(buildApiCallBody(template, { entity, columns, filters: excelExportFilters(filters), fileName, sheetName, userEmail, userId: userEmail }), columns)
          : buildExcelExportRequest({ entity, columns, filters, fileName, sheetName });
        return this.http.post<unknown>(url, body);
      }),
    ).subscribe({
      next: (response) => {
        const file = excelExportFileOf(response);
        if ('error' in file) {
          this.setButtonState(stateId, { busy: false, message: `Export failed: ${file.error.slice(0, 160)}`, kind: 'error' });
          return;
        }
        saveBlob(file.blob, file.fileName);
        const rows = file.rowCount === null ? '' : ` ${file.rowCount} row${file.rowCount === 1 ? '' : 's'}`;
        const note = skipped.length ? ` - not filtered by ${skipped.join(', ')} (no value)` : '';
        this.setButtonState(stateId, { busy: false, message: `Exported${rows}${note}`, kind: 'ok' });
      },
      error: (error: { error?: { message?: string } | string; message?: string }) => {
        const detail = typeof error?.error === 'string' ? error.error : error?.error?.message || error?.message || '';
        this.setButtonState(stateId, { busy: false, message: `Export failed${detail ? ': ' + String(detail).slice(0, 160) : ''}`, kind: 'error' });
      },
    });
  }

  /**
   * The Button's Export: the same export a nested table's Export button runs,
   * for the picked component - its dataset is the entity, and a chosen filter
   * column takes the values in its rows, else the dashboard's selection.
   */
  private runButtonExport(nodeId: string, config: ButtonConfig): void {
    const widgetId = config.exportWidgetId;
    const tile = widgetId ? this.tileFor(widgetId) : undefined;
    if (!widgetId || !tile) {
      this.setButtonState(nodeId, { message: widgetId ? 'Generate the component first - it has no data yet' : 'Pick the component to export', kind: 'error' });
      return;
    }
    const entity = tileDataset(tile);
    const data = this.widgetDataFor(widgetId) ?? {};
    this.exportExcel(nodeId, {
      entity,
      fileName: (config.exportFileName || '').trim() || entity,
      sheetName: config.exportSheetName,
      url: config.exportUrl,
      bodyTemplate: config.exportBody,
      filterColumns: config.exportFilterColumns,
      columns: exportColumnsOf(data, entity),
      rows: rowsIn(data).map((row) => Object.fromEntries(Object.entries(row).filter(([, value]) => !Array.isArray(value)))),
      aliases: nestedColumnAliases(data),
    });
  }

  runTableButton(stateId: string, raw: Record<string, unknown>, extraTokens: Record<string, unknown> = {}): void {
    if (this.buttonStatesState()[stateId]?.busy) return;
    const config = buttonConfigOf(raw);
    this.tableButtonTokens.set(stateId, extraTokens);
    this.runButtonFeature(stateId, config);
  }

  /**
   * A button inside a table cell (TableCellConfig.buttons / an action column's
   * cellButtons): its feature runs like a Button widget's, with the clicked row as
   * context - `{row}` (the whole row) and every column of it as its own token
   * (`{uuid}`, `{cust_spec_no}`, ...) in API / download / approval bodies and
   * navigate targets. `parentRows` = the rows above it in a nested table.
   */
  runCellButton(stateId: string, config: Record<string, unknown>, row: Record<string, unknown>, parentRows: Array<Record<string, unknown>> = []): void {
    const primitives = (source: Record<string, unknown>) =>
      Object.fromEntries(Object.entries(source).filter(([key, value]) => /^\w+$/.test(key) && (value === null || typeof value !== 'object')));
    // Nearest wins: the row's own columns over its parents'.
    const tokens = Object.assign({}, ...parentRows.map(primitives), primitives(row), { row, parentRow: parentRows[parentRows.length - 1] ?? null, parentRows });
    this.runTableButton(stateId, config, tokens);
  }

  /** One button feature, for a Button widget or a table button (`stateId` keys its message and tokens). */
  private runButtonFeature(stateId: string, config: ButtonConfig): void {
    switch (config.action) {
      case 'download': this.runButtonDownload(stateId, config); break;
      case 'api': this.runButtonApis(stateId, config); break;
      case 'filter': this.runButtonFilter(stateId, config); break;
      case 'navigate': this.runButtonNavigate(stateId, config); break;
      case 'popup': this.runButtonPopupScreen(stateId, config); break;
      case 'closePopup': this.closePopupScreen(); break;
      case 'approval': this.runButtonApproval(stateId, config); break;
      case 'export': this.runButtonExport(stateId, config); break;
      case 'saveTable': this.runButtonSaveTable(stateId, config); break;
      case 'upload': this.runButtonUpload(stateId, config); break;
      default: break;
    }
  }

  /**
   * "Save table changes": the table saves its edited rows with its OWN row Save
   * (the save lives in the table, which holds the edits). The request goes out
   * through `tableSaveRequests`; the table answers via `finishTableSave`.
   */
  private runButtonSaveTable(nodeId: string, config: ButtonConfig): void {
    if (config.saveSource === 'values') {
      this.runButtonSaveValues(nodeId, config);
      return;
    }
    const tables = widgetNodes(this.doc.document()).filter((node) => !!node.widget && APPROVAL_TABLE_TYPES.has(node.widget.layoutType));
    const table = config.saveTableId ? tables.find((node) => node.id === config.saveTableId) : tables[0];
    if (!table) {
      this.setButtonState(nodeId, { message: 'Pick the table to save', kind: 'error' });
      return;
    }
    if (table.widget!.layoutType !== 'NESTED_TABLE') {
      this.setButtonState(nodeId, { message: 'This table type saves with its own Save button', kind: 'error' });
      return;
    }
    this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
    // A new number every time: the table saves only on a number it has not
    // handled yet. Counted here, not from the pending request - a finished save
    // removes that, and the next click then sent the same 1 again: the table
    // ignored it and the button kept spinning.
    const seq = (this.tableSaveSeq.get(table.id) ?? 0) + 1;
    this.tableSaveSeq.set(table.id, seq);
    this.tableSaveRequestsState.update((all) => ({ ...all, [table.id]: { seq, buttonId: nodeId } }));
  }

  /**
   * "Save changes" from the dashboard's values: the same update request an input box's
   * Save sends - each business key from the clicked row (a table button) or the
   * current selection, each column from its widget (picked, else the one for that column).
   */
  private runButtonSaveValues(stateId: string, config: ButtonConfig): void {
    const url = config.saveUrl.trim();
    const entity = config.saveEntity.trim();
    const columns = config.saveColumns.filter((entry) => entry.column.trim());
    if (!url || !entity || !columns.length) {
      this.setButtonState(stateId, { busy: false, kind: 'error', message: 'Set the Save API URL, entity and the columns to update' });
      return;
    }
    const rowTokens = this.tableButtonTokens.get(stateId) ?? {};
    const keyValue = (key: string): unknown => {
      const fromRow = rowTokens[key];
      return fromRow !== undefined && fromRow !== null && fromRow !== '' && typeof fromRow !== 'object' ? fromRow : this.currentFilterValue(key);
    };
    const keys = config.saveBusinessKeys;
    const missingKeys = keys.filter((key) => keyValue(key) === null);
    if (missingKeys.length) {
      this.setButtonState(stateId, { busy: false, kind: 'error', message: `Pick a value for ${missingKeys.join(', ')} first` });
      return;
    }
    const values: Record<string, unknown> = {};
    const unmatched: string[] = [];
    columns.forEach(({ column, sourceId }) => {
      const source = sourceId || this.valueSourceFor(column);
      if (!source) unmatched.push(column);
      else values[column.trim()] = typedSaveValue(this.widgetValue(source));
    });
    if (unmatched.length) {
      this.setButtonState(stateId, { busy: false, kind: 'error', message: `No input on the dashboard for ${unmatched.join(', ')} - pick its "Value from"` });
      return;
    }
    const body = updateRequestBody(entity, keys, keyValue, values);
    this.setButtonState(stateId, { busy: true, kind: '', message: '' });
    this.postTableSave$(url, body).subscribe((ok) => this.setButtonState(stateId, {
      busy: false, kind: ok ? 'ok' : 'error', message: ok ? config.saveSuccessMessage || 'Saved' : 'Save failed',
    }));
  }

  /** An input box's value as its widget shows it (formula, "Fill from data", typed), by node id. */
  private readonly shownValues = new Map<string, unknown>();

  setShownValue(nodeId: string, value: unknown): void {
    this.shownValues.set(nodeId, value);
  }

  /** What a value widget shows now: an input box's shown value, a zone slider's handle, typed / dragged, a dropdown's pick, else its saved value. */
  widgetValue(nodeId: string): unknown {
    const textComponent = [...boundTextComponents(this.doc.document())].find(([, text]) => text.id === nodeId)?.[0];
    if (textComponent) {
      const state = this.textValuesState()[textComponent];
      return state?.status === 'ready' ? state.value : null;
    }
    const node = widgetNodes(this.doc.document()).find((item) => item.id === nodeId);
    const layoutType = node?.widget?.layoutType;
    const shown = this.shownValues.get(nodeId);
    if (layoutType === 'INPUT_BOX' && shown !== undefined) return shown;
    if (layoutType === 'ZONE_SLIDER') return this.zoneSliderLevel(nodeId)?.value ?? null;
    const typed = this.inputValues()[nodeId];
    if (typed !== undefined) return typed;
    const tile = this.tileFor(nodeId);
    const picked = tile?.dropdownControl?.value;
    if (picked) return picked.length === 1 ? picked[0] : picked.length ? picked : null;
    const fromData = this.inputDataValues()[nodeId];
    if (fromData?.status === 'ready') return fromData.value;
    if (tile?.inputControl && tile.inputControl.value !== undefined) return tile.inputControl.value;
    return node?.widget?.data?.['value'] ?? null;
  }

  /** The value widget for a column: an input box saving / filled from it, else a dropdown or input on that field. */
  private valueSourceFor(column: string): string {
    const nodes = widgetNodes(this.doc.document()).filter((node) => VALUE_SOURCE_LAYOUTS.has(node.widget?.layoutType ?? ''));
    const matches = (field: unknown) => typeof field === 'string' && !!field && sameFilterColumn(field, column);
    const byBinding = nodes.find((node) => {
      const binding = (node.widget?.binding ?? {}) as Record<string, unknown>;
      const save = binding['save'] as Record<string, unknown> | undefined;
      return matches(save?.['column']) || matches(binding['valueField']);
    });
    if (byBinding) return byBinding.id;
    const byField = nodes.find((node) => {
      const tile = this.tileFor(node.id);
      return matches(tile?.dropdownControl?.field) || matches(tile?.payload?.['field']);
    });
    if (byField) return byField.id;
    // A text showing that column (e.g. Set price: SUM of set_price).
    return [...boundTextComponents(this.doc.document()).values()].find((text) => matches(text.dataBinding?.field))?.id ?? '';
  }

  /**
   * Upload: opens the file picker and POSTs the picked file as multipart/form-data
   * to the Upload API (under "File field name", plus any extra form fields).
   */
  private runButtonUpload(nodeId: string, config: ButtonConfig): void {
    const json = config.uploadMode !== 'multipart';
    // Blank URL = the platform API (/spriced/platform).
    const url = (config.uploadUrl ?? '').trim() ? resolveSelfServiceApiUrl(config.uploadUrl.trim()) : getSelfServicePlatformApiUrl();
    // The picker always opens on the click (browsers allow it only there); the
    // business keys are checked once a file is picked.
    const picker = document.createElement('input');
    picker.type = 'file';
    picker.style.display = 'none';
    document.body.appendChild(picker);
    picker.addEventListener('cancel', () => picker.remove());
    if (config.uploadAccept?.trim()) picker.accept = config.uploadAccept.trim();
    picker.onchange = async () => {
      const file = picker.files?.[0];
      picker.remove();
      if (!file) return;
      const maxMb = Number(config.uploadMaxSizeMb) || 0;
      if (maxMb > 0 && file.size > maxMb * 1024 * 1024) {
        this.setButtonState(nodeId, { message: `"${file.name}" is larger than ${maxMb} MB`, kind: 'error' });
        return;
      }
      if (json) {
        // Keys with no value on the screen are sent empty - the API answers for them.
        const { keys, missing } = this.uploadBusinessKeys(nodeId, config);
        missing.forEach((column) => { keys[column] = ''; });
        if (missing.length) console.warn(`[design-builder] upload: no value on the screen for ${missing.join(', ')} - sent empty`);
        this.sendJsonUpload(nodeId, config, url, file, keys);
        return;
      }
      const form = new FormData();
      // As the Excel add-in sends it: entityName + the file's bytes as a Blob.
      if ((config.uploadEntity ?? '').trim()) form.append('entityName', config.uploadEntity.trim());
      form.append((config.uploadFieldName ?? '').trim() || 'fileContents', new Blob([await readFileBytes(file) as BlobPart], { type: fileMimeType(file) }), file.name);
      const userEmail = this.currentUserEmail();
      const extra = (config.uploadExtraFields ?? '').trim()
        ? buildApiCallBody(config.uploadExtraFields, { ...this.buttonTokens(nodeId, config), fileName: file.name, userEmail, userId: userEmail })
        : null;
      if (extra && typeof extra === 'object') {
        Object.entries(extra as Record<string, unknown>).forEach(([key, value]) =>
          form.append(key, value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')));
      }
      this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
      this.postUpload(nodeId, config, url, form, file.name);
    };
    picker.click();
  }

  /** Upload's business keys: each column's value from the clicked table row, else the dashboard selection. */
  private uploadBusinessKeys(nodeId: string, config: ButtonConfig): { keys: Record<string, unknown>; missing: string[] } {
    const row = this.tableButtonTokens.get(nodeId) ?? {};
    const selections = this.activeFilters().filter((filter) => filter.field && filter.values.length);
    const keys: Record<string, unknown> = {};
    const missing: string[] = [];
    config.uploadKeyColumns.forEach((column) => {
      const fromRow = row[column];
      const selected = selections.find((filter) => filter.field.toLowerCase() === column.toLowerCase())?.values[0];
      const value = fromRow !== undefined && fromRow !== null && fromRow !== '' && typeof fromRow !== 'object' ? fromRow : selected;
      if (value === undefined || value === null || value === '') missing.push(column);
      else keys[column] = value;
    });
    return { keys, missing };
  }

  /** JSON upload: the dataset, business keys and the file's bytes filled into the Upload body. */
  private sendJsonUpload(nodeId: string, config: ButtonConfig, url: string, file: File, businessKeys: Record<string, unknown>): void {
    this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
    readFileBytes(file).then((bytes) => {
      let binary = '';
      for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      // A button saved with the old default body (the file as "file": [bytes]) gets the
      // current default, which sends it as "fileBytes": base64 with its contentType.
      const saved = (config.uploadBody ?? '').trim();
      const template = !saved || saved.replace(/\s+/g, '') === LEGACY_UPLOAD_BODY ? BUTTON_DEFAULTS.uploadBody : saved;
      const userEmail = this.currentUserEmail();
      // Each dashboard selection by its column name (first value), e.g. {cust_spec_no};
      // a button in a table row has that row's columns too (buttonTokens).
      const selected = Object.fromEntries(this.activeFilters()
        .filter((filter) => filter.field && filter.values.length)
        .map((filter) => [filter.field, filter.values[0]]));
      const tokens: Record<string, unknown> = {
        ...selected,
        ...this.buttonTokens(nodeId, config),
        ...businessKeys,
        entity: config.uploadEntity.trim(),
        entityName: config.uploadEntity.trim(),
        businessKeys,
        fileName: file.name,
        fileType: file.type,
        // The MIME type, guessed from the extension when the browser gives none.
        contentType: fileMimeType(file),
        fileSize: file.size,
        // Built only when the body asks for them - both are large.
        ...(template.includes('{fileBase64}') ? { fileBase64: btoa(binary) } : {}),
        ...(template.includes('{fileBytes}') ? { fileBytes: Array.from(bytes) } : {}),
        userEmail,
        userId: userEmail,
      };
      const body = buildApiCallBody(template, tokens);
      // A body saved before Entity / Business keys existed has no {entity} / {businessKeys}:
      // the configured ones are added to its requestData (or its top level) when missing.
      const target = body && typeof body === 'object'
        ? ((body as Record<string, unknown>)['requestData'] && typeof (body as Record<string, unknown>)['requestData'] === 'object'
          ? (body as Record<string, Record<string, unknown>>)['requestData']
          : body as Record<string, unknown>)
        : null;
      if (target) {
        if (config.uploadEntity.trim() && !target['entityName']) target['entityName'] = config.uploadEntity.trim();
        if (Object.keys(businessKeys).length && !target['businessKeys']) target['businessKeys'] = businessKeys;
      }
      this.postUpload(nodeId, config, url, withUploadEnvelope(body), file.name);
    }).catch((error) => {
      console.error('[design-builder] upload: could not build the request', error);
      this.setButtonState(nodeId, { busy: false, message: `Could not upload "${file.name}": ${error?.message ?? error}`, kind: 'error' });
    });
  }

  private postUpload(nodeId: string, config: ButtonConfig, url: string, body: unknown, fileName: string): void {
    this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
    console.info(`[design-builder] upload: POST ${url} - ${fileName}`);
    this.http.post<unknown>(url, body).subscribe({
      next: () => {
        this.setButtonState(nodeId, { busy: false, message: config.uploadSuccessMessage || `Uploaded ${fileName}`, kind: 'ok' });
        // Says why the dashboard does (or does not) reload: the button's own setting decides.
        console.info(`[design-builder] upload: done - ${config.uploadRefresh
          ? 'reloading the dashboard ("Reload the dashboard data after uploading" is on for this button)'
          : 'no reload (switch on "Reload the dashboard data after uploading" to reload)'}`);
        if (config.uploadRefresh && this.applier && this.layout) this.refreshDashboard();
      },
      error: (error: { error?: { message?: string } | string; message?: string }) => {
        const detail = typeof error?.error === 'string' ? error.error : error?.error?.message || error?.message || '';
        this.setButtonState(nodeId, { busy: false, message: `Upload failed${detail ? ': ' + String(detail).slice(0, 160) : ''}`, kind: 'error' });
      },
    });
  }

  /** The last save request number per table node id (only ever grows). */
  private readonly tableSaveSeq = new Map<string, number>();

  /** Pending "save your edited rows" requests, by table node id (read by the table's host). */
  private readonly tableSaveRequestsState = signal<Record<string, { seq: number; buttonId: string }>>({});
  readonly tableSaveRequests = this.tableSaveRequestsState.asReadonly();

  /** The table finished a requested save: the button that asked shows how it went. */
  finishTableSave(tableId: string, result: { saved: number; failed: number; blocked: number }): void {
    const request = this.tableSaveRequestsState()[tableId];
    if (!request) return;
    this.tableSaveRequestsState.update(({ [tableId]: _done, ...rest }) => rest);
    const rows = (count: number) => `${count} row${count === 1 ? '' : 's'}`;
    const notes = [
      result.saved ? `Saved ${rows(result.saved)}` : '',
      result.failed ? `${rows(result.failed)} failed` : '',
      result.blocked ? `${rows(result.blocked)} need their mandatory fields` : '',
    ].filter(Boolean);
    const kind = result.failed || result.blocked ? 'error' : 'ok';
    this.setButtonState(request.buttonId, { busy: false, kind: notes.length ? kind : '', message: notes.join(' · ') || 'Nothing to save' });
  }

  /** `{token}`s in a navigate target / filter value, from the button's tokens (a cell button's row). */
  private fillButtonText(stateId: string, config: ButtonConfig, text: string): string {
    if (!text.includes('{')) return text;
    const tokens = this.buttonTokens(stateId, config);
    return text.replace(/\{(\w+)\}/g, (match, key: string) => {
      const value = tokens[key];
      return value === undefined || value === null || typeof value === 'object' ? match : String(value);
    });
  }

  /** POST a table row's save body — true when it answered OK. */
  postTableSave$(url: string, body: unknown): Observable<boolean> {
    const target = url.trim();
    if (!target) return of(false);
    return this.http.post(resolveSelfServiceApiUrl(target), body).pipe(map(() => true), catchError(() => of(false)));
  }

  /**
   * The value the dashboard is on for a column: a dropdown's pick (value-only ones
   * too), a button / dropdown filter, a filter the opening screen sent, the cross filter.
   * One value as is, several as a list, none = null.
   */
  currentFilterValue(column: string): unknown {
    const pick = (values: unknown[]) => (values.length === 1 ? values[0] : values.length ? values : null);
    for (const tile of this.tiles()) {
      const control = tile.dropdownControl;
      if (control?.field && control.value?.length && sameFilterColumn(control.field, column)) return pick(control.value);
    }
    for (const filter of this.dropdownFilters.values()) {
      if (filter.values.length && sameFilterColumn(filter.field, column)) return pick(filter.values);
    }
    const nav = this.navigationFilter;
    if (nav) {
      const grouped = groupNavigationFilters(navigationEntriesOf(nav).filter((entry) => !this.navigationOverridden.has(entry.key)));
      for (const [field, values] of grouped) if (values.length && sameFilterColumn(field, column)) return pick(values);
    }
    if (this.crossFilter?.values.length && sameFilterColumn(this.crossFilter.column, column)) return pick(this.crossFilter.values);
    return null;
  }

  /**
   * An input box's Save (Data tab "Save button (update)"): the same update request a
   * nested-table row's Save sends - the business keys from the current filters, the
   * box's value in its column. Its progress shows under the box (buttonStates, `<id>:save`).
   */
  saveInputValue(nodeId: string, value: unknown): void {
    const node = widgetNodes(this.doc.document()).find((item) => item.id === nodeId);
    const binding = node?.widget?.binding as Record<string, unknown> | undefined;
    const save = inputSaveConfigOf(binding);
    const stateId = `${nodeId}:save`;
    if (!save || this.buttonStatesState()[stateId]?.busy) return;
    const column = save.column?.trim() || String(binding?.['valueField'] ?? '');
    const entity = save.entity?.trim() || String(binding?.['valueDataset'] ?? '');
    if (!save.url?.trim() || !column || !entity) {
      this.setButtonState(stateId, { busy: false, kind: 'error', message: 'Set the Save API URL, entity and column in the Data tab' });
      return;
    }
    const keys = (save.businessKeys ?? []).filter(Boolean);
    const missing = keys.filter((key) => this.currentFilterValue(key) === null);
    if (missing.length) {
      this.setButtonState(stateId, { busy: false, kind: 'error', message: `Pick a value for ${missing.join(', ')} first` });
      return;
    }
    const body = updateRequestBody(entity, keys, (key) => this.currentFilterValue(key), { [column]: typedSaveValue(value) });
    this.setButtonState(stateId, { busy: true, kind: '', message: '' });
    this.postTableSave$(save.url, body).subscribe((ok) =>
      this.setButtonState(stateId, { busy: false, kind: ok ? 'ok' : 'error', message: ok ? 'Saved' : 'Save failed' }));
  }

  /** Extra body tokens of table buttons, by state id (see runTableButton). */
  private readonly tableButtonTokens = new Map<string, Record<string, unknown>>();

  /** A BUTTON widget was clicked: run its one feature (popups open in the button itself). */
  onButtonClick(nodeId: string): void {
    const node = widgetNodes(this.doc.document()).find((item) => item.id === nodeId);
    if (!node?.widget || this.buttonStatesState()[nodeId]?.busy) return;
    this.runButtonFeature(nodeId, buttonConfigOf(node.widget.appearance));
  }

  private readonly keycloak = inject(KeycloakService, { optional: true });

  /** The signed-in user's email (from the login token); '' when unknown. */
  private currentUserEmail(): string {
    try {
      const token = this.keycloak?.getKeycloakInstance()?.tokenParsed as Record<string, unknown> | undefined;
      const email = token?.['email'] ?? token?.['preferred_username'];
      return typeof email === 'string' ? email : '';
    } catch {
      return '';
    }
  }

  /** Where a zone slider's handle is now: dragged, else its data value, else its drawn value. */
  /**
   * A zone slider's level right now - the block its handle is in, as the slider
   * draws it (levels from columns / static zones, the dragged or bound value).
   * `sliderRef` = the slider's id or layer name; blank = the first zone slider.
   * Reactive: read inside a computed, it follows the handle as it moves.
   */
  zoneSliderLevel(sliderRef = ''): ZoneSliderLevelNow | null {
    const sliders = widgetNodes(this.doc.document()).filter((node) => node.widget?.layoutType === 'ZONE_SLIDER');
    const ref = sliderRef.trim().toLowerCase();
    const node = (ref ? sliders.find((item) => item.id === sliderRef || (item.name ?? '').trim().toLowerCase() === ref) : undefined) ?? (ref ? undefined : sliders[0]);
    if (!node?.widget) return null;
    const compute = this.zoneValuesState()[node.id];
    // What the slider itself draws (its levels may come from dataset rows,
    // which this service has no reader for); rebuilt here only until it renders.
    const data = this.zoneSliderShownState()[node.id] ?? applyZoneSliderBinding(
      zoneSliderStaticData(node.widget), zoneSliderBindingOf(node.widget.binding), () => null,
      compute?.status === 'ready' ? compute.values : null,
    );
    const model = resolveZoneSliderModel(data as ZoneSliderData, this.zoneSliderCurrentValue(node.id));
    const zone = model.activeZone;
    return {
      sliderId: node.id,
      label: zone?.label ?? '',
      index: zone ? model.zones.indexOf(zone) : -1,
      color: zone?.color ?? '',
      value: model.value,
      valueText: model.valueText,
      levels: model.zones.map((item) => item.label).filter(Boolean),
    };
  }

  /**
   * Each zone slider's data exactly as its widget draws it (base currency):
   * value, range and levels - static, from columns or from dataset rows. The
   * level a popup / "Text by level" / "Show at levels" reads then always matches
   * the block the handle is in on screen.
   */
  private readonly zoneSliderShownState = signal<Record<string, Record<string, unknown>>>({});

  setZoneSliderShown(nodeId: string, data: Record<string, unknown>): void {
    if (this.zoneSliderShownState()[nodeId] === data) return;
    this.zoneSliderShownState.update((all) => ({ ...all, [nodeId]: data }));
  }

  zoneSliderCurrentValue(nodeId: string): number | null {
    const typed = this.inputValuesState()[nodeId];
    if (typeof typed === 'number' && Number.isFinite(typed)) return typed;
    const state = this.zoneValuesState()[nodeId];
    const fromData = state?.status === 'ready' ? state.values['value'] : undefined;
    if (typeof fromData === 'number' && Number.isFinite(fromData)) return fromData;
    const node = widgetNodes(this.doc.document()).find((item) => item.id === nodeId);
    const drawn = Number((node?.widget?.appearance ?? {})['value'] ?? (node?.widget?.data ?? {})['value']);
    return Number.isFinite(drawn) ? drawn : null;
  }

  /**
   * Approval: POST the slider handle's value for the record the slider stands
   * for, as the signed-in user - e.g. { uuid, value, userId } to
   * /approval-level/<workflow>/submit.
   */
  private runButtonApproval(nodeId: string, config: ButtonConfig): void {
    const url = (config.approvalUrl ?? '').trim();
    if (!url) {
      this.setButtonState(nodeId, { message: 'Set the approval API', kind: 'error' });
      return;
    }
    const widgets = widgetNodes(this.doc.document());
    const sliders = widgets.filter((node) => node.widget?.layoutType === 'ZONE_SLIDER');
    const isTable = (node: (typeof widgets)[number]) => !!node.widget && APPROVAL_TABLE_TYPES.has(node.widget.layoutType);
    const picked = config.approvalSliderId ? widgets.find((node) => node.id === config.approvalSliderId) : undefined;
    // "Values from" left on its default with no slider on the page: the first table is the source.
    const table = picked && isTable(picked) ? picked : !picked && !sliders.length ? widgets.find(isTable) : undefined;
    if (table) {
      this.runTableApproval(nodeId, config, url, table.id);
      return;
    }
    const slider = sliders.find((node) => node.id === config.approvalSliderId) ?? sliders[0];
    if (!slider) {
      this.setButtonState(nodeId, { message: 'Pick a table or zone slider in "Values from"', kind: 'error' });
      return;
    }
    const value = this.zoneSliderCurrentValue(slider.id);
    const state = this.zoneValuesState()[slider.id];
    const recordId = state?.status === 'ready' ? state.recordId ?? '' : '';
    const body = config.approvalBody || '';
    if (body.includes('{recordId}') && !recordId) {
      this.setButtonState(nodeId, { message: 'No record selected - set the slider\'s Record ID column and pick a spec', kind: 'error' });
      return;
    }
    const userEmail = this.currentUserEmail();
    const tokens = { ...this.buttonTokens(nodeId, config), value, recordId, uuid: recordId, userEmail, userId: userEmail };
    this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
    this.http.post<unknown>(resolveSelfServiceApiUrl(url), buildApiCallBody(body, tokens)).subscribe({
      next: () => {
        this.setButtonState(nodeId, { busy: false, message: config.approvalSuccessMessage || 'Submitted for approval', kind: 'ok' });
        if (config.approvalRefresh && this.applier && this.layout) this.refreshDashboard();
        // Optional: only once the API said OK, go to the configured screen / page.
        if (config.approvalNavigate) this.runButtonNavigate(nodeId, config);
      },
      error: (error: { error?: { message?: string } | string; message?: string }) => {
        const detail = typeof error?.error === 'string' ? error.error : error?.error?.message || error?.message || '';
        this.setButtonState(nodeId, { busy: false, message: `Approval failed${detail ? ': ' + String(detail).slice(0, 160) : ''}`, kind: 'error' });
      },
    });
  }

  /**
   * Approval from a table: one `{ uuid, value }` item per row - the rows the user
   * edited (any cell), or every row - posted as `{ userId, items }` to the batch
   * approval API. `uuid` is read from the ID column (may be hidden), `value` from
   * the Value column, the typed value when that cell was edited.
   */
  private runTableApproval(nodeId: string, config: ButtonConfig, url: string, tableId: string): void {
    const idColumn = (config.approvalUuidColumn || 'uuid').trim();
    const valueColumn = (config.approvalValueColumn || '').trim();
    if (!valueColumn) {
      this.setButtonState(nodeId, { message: 'Set the Value column to send', kind: 'error' });
      return;
    }
    const editedOnly = config.approvalRows !== 'all';
    const rows = editedOnly ? editedRowsOf(this.tableEditsState()[tableId] ?? []) : rowsIn(this.widgetDataFor(tableId));
    const latestEdits = editedRowsOf(this.tableEditsState()[tableId] ?? []);
    if ([...rows, ...latestEdits].some((row) => isNegativeNumber(rowColumnValue(row, valueColumn)))) {
      this.setButtonState(nodeId, { busy: false, message: `"${valueColumn}" cannot be negative`, kind: 'error' });
      return;
    }
    if (!rows.length) {
      this.setButtonState(nodeId, { message: editedOnly ? 'Nothing to submit - edit a row in the table first' : 'The table has no rows', kind: 'error' });
      return;
    }
    const { items, missingId } = approvalItemsOf(rows, idColumn, valueColumn);
    if (!items.length) {
      this.setButtonState(nodeId, { message: `No "${idColumn}" in the rows - add the ID column to the table (it can be hidden)`, kind: 'error' });
      return;
    }
    if (missingId) console.warn(`[design-builder] approval: ${missingId} row(s) skipped - no "${idColumn}" value`);

    const userEmail = this.currentUserEmail();
    const template = config.approvalBody || '';
    const body = template.includes('{items}')
      ? buildApiCallBody(template, { ...this.buttonTokens(nodeId, config), items, userEmail, userId: userEmail })
      : { userId: userEmail, items };
    this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
    this.http.post<unknown>(resolveSelfServiceApiUrl(url), body).subscribe({
      next: () => {
        // Submitted: those edits are no longer pending.
        if (editedOnly) this.tableEditsState.update(({ [tableId]: _done, ...rest }) => rest);
        const done = config.approvalSuccessMessage || 'Submitted for approval';
        this.setButtonState(nodeId, { busy: false, message: `${done} (${items.length} row${items.length === 1 ? '' : 's'})`, kind: 'ok' });
        if (config.approvalRefresh && this.applier && this.layout) this.refreshDashboard();
        if (config.approvalNavigate) this.runButtonNavigate(nodeId, config);
      },
      error: (error: { error?: { message?: string } | string; message?: string }) => {
        const detail = typeof error?.error === 'string' ? error.error : error?.error?.message || error?.message || '';
        this.setButtonState(nodeId, { busy: false, message: `Approval failed${detail ? ': ' + String(detail).slice(0, 160) : ''}`, kind: 'error' });
      },
    });
  }

  /** Disable a table-backed approval button while any submitted value is negative. */
  approvalHasNegativeValue(nodeId: string): boolean {
    const button = widgetNodes(this.doc.document()).find((node) => node.id === nodeId);
    if (!button?.widget) return false;
    const config = buttonConfigOf(button.widget.appearance);
    if (config.action !== 'approval' || !config.approvalValueColumn?.trim()) return false;
    const widgets = widgetNodes(this.doc.document());
    const sliders = widgets.filter((node) => node.widget?.layoutType === 'ZONE_SLIDER');
    const isTable = (node: (typeof widgets)[number]) => !!node.widget && APPROVAL_TABLE_TYPES.has(node.widget.layoutType);
    const picked = config.approvalSliderId ? widgets.find((node) => node.id === config.approvalSliderId) : undefined;
    const table = picked && isTable(picked) ? picked : !picked && !sliders.length ? widgets.find(isTable) : undefined;
    if (!table) return false;
    const rows = config.approvalRows !== 'all'
      ? editedRowsOf(this.tableEditsState()[table.id] ?? [])
      : rowsIn(this.widgetDataFor(table.id));
    const latestEdits = editedRowsOf(this.tableEditsState()[table.id] ?? []);
    return [...rows, ...latestEdits].some((row) => isNegativeNumber(rowColumnValue(row, config.approvalValueColumn)));
  }

  /** Open popup → a screen: only that source reaches the runtime. */
  private runButtonPopupScreen(nodeId: string, config: ButtonConfig): void {
    if (config.popupSource !== 'screen') return;
    if (!config.popupScreenId) {
      this.setButtonState(nodeId, { message: 'Pick a popup screen', kind: 'error' });
      return;
    }
    this.popupScreen.set({ screenId: config.popupScreenId, buttonId: nodeId, seq: (this.popupScreen()?.seq ?? 0) + 1 });
  }

  closePopupScreen(): void {
    this.popupScreen.set(null);
  }

  /** Render data of any widget node: live interaction data first, else what the canvas shows. */
  widgetDataFor(nodeId: string): Record<string, unknown> | null {
    const live = this.dataFor(nodeId);
    if (live) return live;
    const tile = this.tileFor(nodeId);
    const fromTile = tile ? tileRenderData(tile) : null;
    if (fromTile) return fromTile;
    const node = widgetNodes(this.doc.document()).find((item) => item.id === nodeId);
    return node?.widget ? resolveWidgetData(node.widget) : null;
  }

  private runButtonDownload(nodeId: string, config: ButtonConfig): void {
    if (config.downloadSource === 'excel') {
      const tile = config.downloadWidgetId ? this.tileFor(config.downloadWidgetId) : null;
      const entity = (config.downloadEntity || '').trim() || (tile ? tileDataset(tile) : '');
      this.exportExcel(nodeId, {
        entity,
        columns: config.downloadWidgetId ? exportColumnsOf(this.widgetDataFor(config.downloadWidgetId), entity) : [],
        fileName: config.downloadFileName && config.downloadFileName !== 'export' ? config.downloadFileName : entity,
        sheetName: config.downloadSheetName,
      });
      return;
    }
    if (config.downloadSource === 'widget') {
      const data = config.downloadWidgetId ? this.widgetDataFor(config.downloadWidgetId) : null;
      const rows = rowsIn(data);
      if (!rows.length) {
        this.setButtonState(nodeId, { message: config.downloadWidgetId ? 'No data to download' : 'Pick a widget to download', kind: 'error' });
        return;
      }
      const json = config.downloadFormat === 'json';
      const content = json ? JSON.stringify(rows, null, 2) : rowsToCsv(rows);
      const type = json ? 'application/json' : 'text/csv;charset=utf-8';
      saveBlob(new Blob([content], { type }), withExtension(config.downloadFileName, json ? 'json' : 'csv'));
      this.setButtonState(nodeId, { message: `Downloaded ${rows.length} rows`, kind: 'ok' });
      return;
    }

    const url = config.downloadUrl.trim();
    if (!url) {
      this.setButtonState(nodeId, { message: 'Set a download URL', kind: 'error' });
      return;
    }
    const method = (config.downloadMethod || 'GET').toUpperCase();
    const hasBody = method !== 'GET' && method !== 'DELETE';
    this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
    this.http
      .request(method, resolveSelfServiceApiUrl(url), {
        body: hasBody ? buildApiCallBody(config.downloadBody, this.buttonTokens(nodeId, config)) : undefined,
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          const blob = response.body ?? new Blob([]);
          const name = fileNameFromDisposition(response.headers.get('content-disposition'))
            || withExtension(config.downloadFileName, extensionForType(blob.type));
          saveBlob(blob, name);
          this.setButtonState(nodeId, { busy: false, message: `Downloaded ${name}`, kind: 'ok' });
        },
        error: () => this.setButtonState(nodeId, { busy: false, message: 'Download failed', kind: 'error' }),
      });
  }

  private runButtonApis(nodeId: string, config: ButtonConfig): void {
    const apis = config.apis.filter((api) => !!api?.url?.trim());
    if (!apis.length) {
      this.setButtonState(nodeId, { message: 'Add an API to call', kind: 'error' });
      return;
    }
    const tokens = this.buttonTokens(nodeId, config);
    const targetIds = config.apiTargets;
    const call = (api: ButtonApiCall) => this.buttonApiCall$(api, tokens).pipe(
      map((response) => ({ ok: true, response: response as Record<string, unknown> | null })),
      catchError(() => of({ ok: false, response: null as Record<string, unknown> | null })),
    );
    const all$ = config.apiRunMode === 'parallel'
      ? forkJoin(apis.map(call))
      : concat(...apis.map(call)).pipe(toArray());

    this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
    const stamp = this.beginRecompute(targetIds);
    all$.subscribe((outcomes) => {
      // A `{ results: [...] }` envelope refreshes the target widgets, as an input's recompute does.
      outcomes.forEach(({ response }) => {
        if (response && Array.isArray(response['results'])) this.applyResults(response, targetIds, undefined, { stamp });
      });
      this.markBusy([]);
      const failed = outcomes.filter((outcome) => !outcome.ok).length;
      this.setButtonState(nodeId, failed
        ? { busy: false, message: `${failed} of ${outcomes.length} API call${outcomes.length > 1 ? 's' : ''} failed`, kind: 'error' }
        : { busy: false, message: config.apiSuccessMessage, kind: 'ok' });
    });
  }

  private buttonApiCall$(api: ButtonApiCall, tokens: Record<string, unknown>): Observable<Record<string, unknown>> {
    const method = (api.method || 'POST').toUpperCase();
    const hasBody = method !== 'GET' && method !== 'DELETE';
    return this.http.request<unknown>(method, resolveSelfServiceApiUrl(api.url.trim()), {
      headers: parseHeaders(api.headers),
      body: hasBody ? buildApiCallBody(api.body, tokens) : undefined,
      params: hasBody ? undefined : buildApiCallParams({ componentId: tokens['componentId'] }),
    }).pipe(map((response) => (response && typeof response === 'object' ? response as Record<string, unknown> : { value: response })));
  }

  /**
   * Apply (or, for a toggle button that is already on, clear) the button's
   * field = value filter on its targets — the same server recompute a dropdown
   * filter makes, so the targets refresh exactly as they would from a dropdown.
   */
  private runButtonFilter(nodeId: string, config: ButtonConfig): void {
    const field = config.filterField.trim();
    const targetIds = config.filterTargets;
    if (!field || !targetIds.length) {
      this.setButtonState(nodeId, { message: !field ? 'Set the filter field' : 'Pick at least one target', kind: 'error' });
      return;
    }
    const clearing = config.filterToggle && !!this.buttonStatesState()[nodeId]?.active;
    const values = clearing ? [] : filterValuesOf(config);
    const key = BUTTON_FILTER_PREFIX + nodeId;
    if (values.length) this.dropdownFilters.set(key, { field, values, textTargets: [] });
    else this.dropdownFilters.delete(key);
    this.publishFilters();

    const tiles = this.tiles();
    const byComponent = new Map(tiles.map((tile) => [tile.componentId, tile]));
    const targets = targetIds.map((componentId) => ({ componentId, label: byComponent.get(componentId)?.title || componentId, field }));
    const dataset = targetIds
      .map((componentId) => byComponent.get(componentId))
      .map((tile) => (tile ? tileDataset(tile) : ''))
      .find(Boolean) ?? '';
    const componentId = `CMP-BUTTON-${nodeId}`;
    const payload: Record<string, unknown> = {
      componentId,
      componentType: 'INPUT',
      inputType: 'DROPDOWN',
      ...(dataset ? { dataset } : {}),
      field,
      value: values,
      targets,
      otherInputs: this.otherInputsFor(tiles, componentId),
    };
    const url = config.filterApiUrl.trim();
    const apiConfig = url
      ? { enabled: true, url: resolveSelfServiceApiUrl(url), method: config.filterApiMethod, bodyTemplate: config.filterApiBody, mode: 'replace' as const, targets: targetIds }
      : undefined;

    this.setButtonState(nodeId, { busy: true, message: '', kind: '' });
    const stamp = this.beginRecompute(targetIds);
    this.ensureSaved(targetIds)
      .pipe(switchMap(() => this.inputCompute.resolve(apiConfig, this.generateUrl(), this.withTargetCanvasFilters(payload, targetIds), { ...this.buttonTokens(nodeId, config), ...payload, value: values, field })))
      .subscribe({
        next: (res) => {
          this.applyResults(res, targetIds, undefined, { stamp });
          this.setButtonState(nodeId, { busy: false, active: values.length > 0 && config.filterToggle, message: '', kind: '' });
        },
        error: () => {
          this.markBusy([]);
          this.setButtonState(nodeId, { busy: false, message: 'Filter failed', kind: 'error' });
        },
      });
  }

  private runButtonNavigate(nodeId: string, config: ButtonConfig): void {
    const target = this.fillButtonText(nodeId, config, config.navigateTarget.trim());
    if (config.navigateType === 'screen') {
      if (!config.navigateScreenId) {
        this.setButtonState(nodeId, { message: 'Pick a screen', kind: 'error' });
        return;
      }
      this.screenRequest.set({ screenId: config.navigateScreenId, seq: (this.screenRequest()?.seq ?? 0) + 1 });
      if (this.designMode()) this.setButtonState(nodeId, { message: 'Switches screen in Preview', kind: 'ok' });
      return;
    }
    if (config.navigateType !== 'landing' && !target) {
      this.setButtonState(nodeId, { message: 'Set where to navigate', kind: 'error' });
      return;
    }
    // Leaving the builder would end the edit session: Preview / published only.
    if (this.designMode()) {
      this.setButtonState(nodeId, { message: 'Navigation runs in Preview', kind: 'ok' });
      return;
    }

    if (config.navigateType === 'url') {
      window.open(target, config.navigateNewTab ? '_blank' : '_self', 'noopener');
      return;
    }
    const filterKey = (config.navigateFilterKey ?? '').trim();
    const filterValue = this.fillButtonText(nodeId, config, (config.navigateFilterValue ?? '').trim());
    const entries: NavigationFilterEntry[] = filterKey && filterValue ? [{ key: filterKey, value: filterValue }] : [];
    // "Send this screen's selections": every dropdown filter goes along, so the
    // next screen opens on the same customer / spec (a fixed key above wins).
    if (config.navigateSendFilters === true) {
      this.activeFilters().forEach((filter) => {
        if (!filter.field || entries.some((entry) => entry.key === filter.field)) return;
        filter.values.forEach((value) => entries.push({ key: filter.field, value: String(value) }));
      });
    }
    const params = navigationFilterQueryParams(entries);
    const queryParams = Object.keys(params).length ? params : undefined;
    // Landing = the app root, which redirects to the configured LANDING_ROUTE.
    const tree = config.navigateType === 'dashboard'
      ? this.router.createUrlTree(['/dvm-published', target], { queryParams })
      : this.router.createUrlTree([config.navigateType === 'landing' ? '/' : '/' + target.replace(/^\/+/, '')], { queryParams });
    if (config.navigateNewTab) window.open(this.router.serializeUrl(tree), '_blank', 'noopener');
    else void this.router.navigateByUrl(tree);
  }

  /** `{token}` values a button's request bodies can use. */
  private buttonTokens(nodeId: string, config: ButtonConfig): Record<string, unknown> {
    return {
      componentId: `CMP-BUTTON-${nodeId}`,
      label: config.label,
      timestamp: new Date().toISOString(),
      filters: this.activeFilters().map((filter) => ({ field: filter.field, values: filter.values })),
      // Every edited table cell: [{ table, level, path, field, value, row }].
      tableEdits: Object.values(this.tableEditsState()).flat(),
      ...(this.tableButtonTokens.get(nodeId) ?? {}),
    };
  }

  private setButtonState(nodeId: string, patch: Partial<ButtonRunState>): void {
    this.buttonStatesState.update((all) => ({ ...all, [nodeId]: { ...EMPTY_BUTTON_STATE, ...all[nodeId], ...patch } }));
  }

  // ── Targets (for highlighting and the text's "Filtered by" list) ──────────

  /** Node ids a widget drives: its chart / input targets and its text targets. */
  targetNodeIds(nodeId: string): ReadonlySet<string> {
    const tile = this.tileFor(nodeId);
    if (!tile) return EMPTY_IDS;
    const byComponent = new Map(this.tiles().map((item) => [item.componentId, item.id]));
    const componentIds = [
      ...targetIdsOf(tile.payload?.['targets']),
      ...(tile.dropdownControl?.targets ?? []),
      ...(tile.inputControl?.targets ?? []),
      ...(tile.fileUploadControl?.targets ?? []),
    ].map(String);
    const ids = new Set(componentIds.map((id) => byComponent.get(id)).filter((id): id is string => !!id));
    textTargetsOf(tile).forEach((id) => ids.add(id));
    return ids;
  }

  /** Generated widgets that filter this text box. */
  filtersOf(textNodeId: string): Array<{ nodeId: string; title: string }> {
    return this.tiles()
      .filter((tile) => textTargetsOf(tile).includes(textNodeId))
      .map((tile) => ({ nodeId: tile.id, title: tile.title || tile.layoutType }));
  }

  /** Widgets that can filter text: generated dropdowns and chart cross-filter sources. */
  textFilterSources(): Array<{ nodeId: string; title: string }> {
    return this.tiles()
      .filter((tile) => tile.layoutType === 'DROPDOWN' || !!CLIENT_CHART_CROSS_FILTER[tile.layoutType] || tile.layoutType === 'WATERFALL')
      .map((tile) => ({ nodeId: tile.id, title: tile.title || tile.layoutType }));
  }

  /** Link / unlink a text box as a target of a filter widget (saved on that widget's build). */
  setTextTarget(filterNodeId: string, textNodeId: string, linked: boolean): void {
    const node = widgetNodes(this.doc.document()).find((item) => item.id === filterNodeId);
    const build = node?.widget?.build;
    if (!node?.widget || !build) return;
    const current = ((build['textTargets'] as string[] | undefined) ?? []).filter((id) => id !== textNodeId);
    const textTargets = linked ? [...current, textNodeId] : current;
    const dropdownControl = build['dropdownControl'] as Record<string, unknown> | undefined;
    const nextBuild: Record<string, unknown> = { ...build, textTargets };
    if (dropdownControl) {
      const others = ((dropdownControl['targets'] as string[] | undefined) ?? []).filter((id) => !String(id).startsWith(TEXT_TARGET_PREFIX));
      nextBuild['dropdownControl'] = { ...dropdownControl, targets: [...others, ...textTargets.map((id) => TEXT_TARGET_PREFIX + id)] };
    }
    this.forget(filterNodeId);
    this.doc.updateNode(filterNodeId, { widget: { ...node.widget, build: nextBuild } }, linked ? 'Link text to filter' : 'Unlink text from filter');
    this.publishFilters();
  }

  // ── Saving target definitions ──────────────────────────────────────────────

  /** componentIds whose definition the backend has (saved this session). */
  private readonly savedComponentIds = new Set<string>();

  /** A chart was (re)generated and saved by the build form. */
  markSaved(componentId: string): void {
    if (componentId) this.savedComponentIds.add(componentId);
  }

  /**
   * The backend recomputes an input's targets from their STORED definitions
   * (component_definitions), so a target that was never saved fails with
   * "No component found". Save any not saved yet before the input fires.
   */
  private ensureSaved(targetIds: string[]): Observable<unknown> {
    const wanted = new Set(targetIds);
    const unsaved = this.tiles().filter((tile) =>
      wanted.has(tile.componentId) && !this.savedComponentIds.has(tile.componentId) && !!tile.payload && Object.keys(tile.payload).length > 0);
    if (!unsaved.length) return of(null);
    return forkJoin(unsaved.map((tile) => this.saveDefinition(tile.componentId, tile.payload)));
  }

  /** POST the definition to the backend's component store; failures do not block the recompute. */
  saveDefinition(componentId: string, payload: Record<string, unknown>): Observable<unknown> {
    return this.http.post(this.saveUrl(), { ...payload, componentId }).pipe(
      tap(() => this.markSaved(componentId)),
      catchError(() => of(null)),
    );
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  /** `saveDbConfigApi` when configured, else the component store the compute API reads targets from. */
  private saveUrl(): string {
    return resolveSelfServiceApiUrl(this.layout?.apiEndpoints?.saveDbConfigApi, '/components');
  }

  /** Write compute results addressed to registered texts into their value store. */
  private applyTextResults(results: Array<Record<string, unknown>>): void {
    const texts = boundTextComponents(this.doc.document());
    if (!texts.size) return;
    const next = { ...this.textValuesState() };
    let changed = false;
    results.forEach((entry) => {
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const componentId = String(entry?.['componentId'] ?? result?.['componentId'] ?? '').trim();
      const node = componentId ? texts.get(componentId) : undefined;
      if (!node?.dataBinding) return;
      // Same reading as the text's own compute (connectText): a filter change
      // must not turn a "Count per status" text into a plain value ("—").
      next[componentId] = textValueFromResult(node.dataBinding, result);
      changed = true;
    });
    if (changed) this.textValuesState.set(next);
  }

  private applyResults(response: Record<string, unknown> | null, targetIds: string[], firstValueIds: ReadonlySet<string> = new Set(),
    options: ApplyResultsOptions = {}): ReadonlySet<string> {
    // A refresh answer - or an older interaction's - must not clear the
    // "Updating data…" overlay of an interaction still running.
    if (!options.refresh && (options.stamp === undefined || options.stamp >= this.latestInteractionStamp)) this.markBusy([]);
    const answered = (response?.['results'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(answered) || !answered.length) return new Set();
    // Entries a newer interaction has asked for since are not this answer's to apply.
    const stamp = options.stamp;
    const results = stamp === undefined ? answered : answered.filter((entry) => {
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const componentId = String(entry?.['componentId'] ?? result?.['componentId'] ?? '').trim();
      return !componentId || !this.supersededSince(componentId, stamp);
    });
    if (!results.length) return new Set();
    // Texts first: they are components, not tiles, so they must land even when
    // no build host is attached to apply the chart results.
    this.applyTextResults(results);
    if (!this.applier && !firstValueIds.size) return new Set();
    const allowed = new Set(targetIds);
    const changed = new Map<string, DashboardTile>();
    const emptied = new Set(this.noDataState());
    results.forEach((entry) => {
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const componentId = String(entry?.['componentId'] ?? result?.['componentId'] ?? '').trim();
      const current = [...this.tiles().map((tile) => changed.get(tile.id) ?? tile)];
      const target = findRecomputeTargetTile(current, componentId, resolveRecomputeChartType(entry), allowed);
      if (!target) return;
      if (firstValueIds.has(target.id) && target.dropdownControl) {
        // Opt-in dropdowns may return field-keyed rows or an explicit list,
        // rather than the legacy [{ value: ... }] input response.
        if (!['data', 'options', 'optionsList', 'values', 'responseList'].some((key) => Array.isArray(result[key]))) return;
        const control = target.dropdownControl;
        const data = control.widgetData ?? {};
        let options = dropdownOptionsFromComputeResponse(result, control.field);
        if (!options.length && Array.isArray(result['data'])) {
          options = (result['data'] as unknown[]).map((item) => {
            const value = item && typeof item === 'object'
              ? (item as Record<string, unknown>)[control.field] ?? (item as Record<string, unknown>)['value']
              : item;
            return value == null ? '' : String(value);
          }).filter(Boolean);
        }
        this.dataOptions.set(target.id, options);
        options = orderDropdownOptions(withStaticOptions(staticDropdownOptions(target.payload), options),
          normalizeDropdownOptionOrderMode(data['optionOrderMode']), String(data['customOptionOrder'] ?? ''));
        // A value the opening screen sent stays selected (and listed) through this refresh.
        const held = this.navigationDropdowns.get(target.id);
        if (held) options = [...held.values.filter((value) => !options.includes(value)), ...options];
        // A dropdown from the published design has no `value` until its options
        // load: when this answer comes first, it simply has none selected.
        const value = held ? held.values : (control.value ?? []).filter((value) => options.includes(value));
        changed.set(target.id, { ...target, payload: { ...target.payload, value }, dropdownControl: { ...control, options, value } });
        return;
      }
      // An empty entry is normally a placeholder for a chart the compute did not
      // recompute, so it is skipped. A table's own refresh answering an explicit
      // empty `data` is different: the table really has no rows now.
      const hasData = recomputeEntryHasData(entry);
      if (hasData && !computeEntryError(result)) emptied.delete(target.id);
      // A chart this request asked for answered "no rows" (not an error): it now
      // shows No data - never the rows of an earlier filter or the design.
      else if (!target.dropdownControl && !target.inputControl && (allowed.has(componentId) || options.answerFor === target.id)
        && isExplicitEmptyAnswer(entry)) emptied.add(target.id);
      if (!this.applier || !(hasData || (options.answerFor === target.id && isExplicitEmptyTable(entry, target)))) return;
      const next = { ...target };
      if (this.applier!.applyRecomputeResult(next, entry)) {
        if (next.dropdownControl) this.dataOptions.set(next.id, dataDropdownOptions(result, next.dropdownControl.field ?? ''));
        // A value the opening screen sent stays selected through option refreshes.
        const held = this.navigationDropdowns.get(next.id);
        if (held && next.dropdownControl) {
          const options = [...held.values.filter((value) => !(next.dropdownControl!.options ?? []).includes(value)), ...(next.dropdownControl.options ?? [])];
          next.dropdownControl = { ...next.dropdownControl, options, value: held.values };
          next.payload = { ...next.payload, value: held.values };
        }
        changed.set(next.id, next);
      }
    });
    if (changed.size) this.commit([...changed.values()], options.refresh ? 'refresh' : 'interaction');
    if (emptied.size !== this.noDataState().size || [...emptied].some((id) => !this.noDataState().has(id))) this.noDataState.set(emptied);
    return new Set(changed.keys());
  }

  private applyCrossFilter(): void {
    const filter = this.crossFilter;
    const tiles = this.tiles();
    const source = filter ? tiles.find((tile) => tile.id === filter.sourceTileId) : undefined;
    const listeners = new Set(source ? effectiveListenerTileIds(source, tiles, null) : []);
    const filtered = applyCrossFilterToClientTiles(tiles, filter, listeners);
    this.commit(filtered);
    filtered.forEach((tile) => {
      if (!isRecomputeCrossFilterTile(tile) || (filter && tile.id === filter.sourceTileId)) return;
      if (filter && listeners.has(tile.id)) this.recomputeWaterfall(tile, filter);
      else this.restoreWaterfall(tile);
    });
  }

  private publishFilters(): void {
    const filters: ActiveFilter[] = [...this.dropdownFilters.values()].filter((f) => f.field && f.values.length);
    if (this.crossFilter) {
      const source = this.tileFor(this.crossFilter.sourceTileId);
      filters.push({ field: this.crossFilter.column, values: this.crossFilter.values, textTargets: source ? textTargetsOf(source) : [] });
    }
    const before = JSON.stringify(this.activeFilters().map((filter) => [filter.field, filter.values]));
    this.activeFilters.set(filters);
    // Zone sliders are not dropdown targets on the backend (their numbers are
    // separate components), so a changed filter recomputes them here.
    if (JSON.stringify(filters.map((filter) => [filter.field, filter.values])) !== before) this.scheduleZoneSliderRefresh();
  }

  private zoneSliderRefreshQueued = false;

  /** One refresh per burst of filter changes (a navigation sets several at once). */
  private scheduleZoneSliderRefresh(): void {
    if (this.zoneSliderRefreshQueued || !this.layout || this.heldForCascade()) return;
    this.zoneSliderRefreshQueued = true;
    queueMicrotask(() => {
      this.zoneSliderRefreshQueued = false;
      this.refreshZoneSliders();
      this.refreshInputData();
      this.refreshColumnValues();
    });
  }

  /** Drop filters whose dropdown is no longer on the canvas. */
  private pruneDropdownFilters(tiles: DashboardTile[]): void {
    const live = new Set(tiles.map((tile) => tile.componentId));
    const buttons = new Set(widgetNodes(this.doc.document()).map((node) => BUTTON_FILTER_PREFIX + node.id));
    [...this.dropdownFilters.keys()].forEach((key) => {
      if (!live.has(key) && !buttons.has(key)) this.dropdownFilters.delete(key);
    });
  }

  private recomputeWaterfall(tile: DashboardTile, filter: CrossFilter): void {
    const seq = (this.waterfallSeq.get(tile.id) ?? 0) + 1;
    this.waterfallSeq.set(tile.id, seq);
    const basePayload = this.navigationPayload(tile);
    const payload = { ...basePayload, filters: [...((basePayload['filters'] as unknown[]) ?? []), { column: filter.column, operator: 'IN', values: filter.values }] };
    this.widgetApi.resolve(this.generateUrl(), payload, tile.layoutType).pipe(takeUntil(this.resetRequests)).subscribe({
      next: (response) => {
        if (this.waterfallSeq.get(tile.id) !== seq) return;
        const result = widgetApiResult(response, tile.componentId);
        const raw = (Array.isArray(result) ? result : (result as { data?: WaterfallBar[] })?.data || []) as WaterfallBar[];
        const current = this.tileFor(tile.id);
        if (!raw.length || !current) return;
        const bars = applyStepSignsToWaterfallBars(raw, tile.payload);
        const next: DashboardTile = { ...current, waterfallBarsAll: current.waterfallBarsAll ?? current.waterfallBars, waterfallBars: bars };
        next.previewOption = buildWaterfallPreviewOption(bars, next.format);
        next.waterfallData = buildWaterfallTileData(next);
        this.commit([next]);
      },
      error: () => undefined,
    });
  }

  private restoreWaterfall(tile: DashboardTile): void {
    this.waterfallSeq.set(tile.id, (this.waterfallSeq.get(tile.id) ?? 0) + 1);
    if (!tile.waterfallBarsAll) return;
    const next: DashboardTile = { ...tile, waterfallBars: tile.waterfallBarsAll, waterfallBarsAll: undefined };
    next.previewOption = buildWaterfallPreviewOption(next.waterfallBars!, next.format);
    next.waterfallData = buildWaterfallTileData(next);
    this.commit([next]);
  }

  /** Fire-and-forget backend notification, as the Layout Builder sends it. */
  private postCrossFilterEvent(source: DashboardTile, column: string, values: string[]): void {
    const dataset = tileDatasets(source)[0];
    if (!dataset) return;
    const body = {
      uuid: null,
      crossFilter: { column, values, sourceTileId: source.id, listenerTileIds: effectiveListenerTileIds(source, this.tiles(), null) },
      filters: [{ column, operator: 'IN', values }],
    };
    this.http.post(buildSelfServiceApiUrl(`/${encodeURIComponent(dataset)}/dynamic_filter`), body).pipe(catchError(() => of(null))).subscribe();
  }

  /**
   * Store tiles that differ from what the canvas would render on its own.
   * `origin` tracks which live tiles hold only refreshed data (dropped again
   * when Preview closes) and which hold interaction state (kept).
   */
  private commit(tiles: DashboardTile[], origin: 'interaction' | 'refresh' = 'interaction'): void {
    const base = new Map(this.tiles().map((tile) => [tile.id, tile]));
    const next = new Map(this.live());
    let changed = false;
    tiles.forEach((tile) => {
      if (base.get(tile.id) === tile) return;
      // A tile an interaction already holds keeps that standing; a refresh only refreshes it.
      if (origin === 'interaction') this.refreshedTiles.delete(tile.id);
      else if (!this.live().has(tile.id) || this.refreshedTiles.has(tile.id)) this.refreshedTiles.add(tile.id);
      next.set(tile.id, tile);
      changed = true;
    });
    if (changed) this.live.set(next);
    setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
  }

  private markBusy(componentIds: string[]): void {
    this.busy.set(new Set(componentIds.filter(Boolean)));
  }

  /**
   * An interaction (dropdown, input, upload, button) starts recomputing
   * `targetIds`: show them busy, and stamp them so a refresh that started
   * earlier cannot overwrite this newer, filtered answer.
   */
  private beginRecompute(targetIds: string[]): number {
    this.markBusy(targetIds);
    const stamp = ++this.requestClock;
    this.latestInteractionStamp = stamp;
    const ids = targetIds.filter(Boolean);
    // No targets: the compute answers for whichever components it recomputes.
    if (!ids.length) this.anyComponentStamp = stamp;
    ids.forEach((id) => this.componentStamps.set(id, stamp));
    return stamp;
  }

  /** Whether an interaction asked for `componentId` after the refresh stamped `stamp` started. */
  private supersededSince(componentId: string, stamp: number): boolean {
    return this.anyComponentStamp > stamp || (this.componentStamps.get(componentId) ?? 0) > stamp;
  }

  /** Counts a refresh request in flight until it answers, fails or is cancelled. */
  private trackRefresh<T>(request$: Observable<T>): Observable<T> {
    this.refreshesInFlight++;
    return request$.pipe(finalize(() => { this.refreshesInFlight--; }));
  }

  /** Clears `pending` for a tile - only when the refresh that set it is the one answering. */
  private releasePending(tileId: string, version: number): void {
    if (this.pendingRefreshOwners.get(tileId) !== version) return;
    this.pendingRefreshOwners.delete(tileId);
    this.setPending(tileId, false);
  }

  /**
   * Show a widget's loading state while the Data tab generates it. Some chart
   * steps build the tile themselves and never report failure, so a mark that
   * is not cleared by a write gives up after a minute rather than spin forever.
   */
  markGenerating(nodeId: string, generating: boolean): void {
    clearTimeout(this.generatingTimers.get(nodeId));
    this.generatingTimers.delete(nodeId);
    if (generating) this.generatingTimers.set(nodeId, setTimeout(() => this.markGenerating(nodeId, false), 60_000));
    this.setPending(nodeId, generating);
  }

  private readonly generatingTimers = new Map<string, ReturnType<typeof setTimeout>>();

  private setPending(nodeId: string, pending: boolean): void {
    if (this.pendingState().has(nodeId) === pending) return;
    const next = new Set(this.pendingState());
    if (pending) next.add(nodeId);
    else next.delete(nodeId);
    this.pendingState.set(next);
  }

  private setUploadMessage(nodeId: string, message: string): void {
    this.uploadMessages.update((all) => ({ ...all, [nodeId]: message }));
  }

  private generateUrl(): string {
    return resolveSelfServiceApiUrl(this.layout?.apiEndpoints?.generateConfigApi, LEGACY_GENERATE_PATH);
  }
}

/** Payload used for initial hydration / Refresh. Lazy nested tables send only
 * their level-0 TABLE request; all other chart families keep their full payload. */
export function initialChartComputePayload(tile: DashboardTile): Record<string, unknown> {
  const levelQueries = Array.isArray(tile.payload?.['levelQueries'])
    ? tile.payload['levelQueries'] as Array<Record<string, unknown>>
    : [];
  return tile.layoutType === 'NESTED_TABLE' && levelQueries[0]
    ? structuredClone(levelQueries[0])
    : tile.payload;
}

/** Prefix marking a text box in a target list (texts have no componentId). */
export const TEXT_TARGET_PREFIX = 'text:';

export interface ActiveFilter {
  field: string;
  values: string[];
  /** Text node ids this filter applies to — text is opt-in, never implicit. */
  textTargets: string[];
}

export interface DashboardNavigationFilter {
  filterKey: string;
  filterValue: string;
  /** Every filter the navigation sent (customer name AND spec …); absent = just filterKey / filterValue. */
  filters?: NavigationFilterEntry[];
  targetComponents: string[];
}

/**
 * Filters as /components/compute reads them - `{ dataset, field, operator, value }`,
 * one per dataset of the component. The backend skips a filter without a
 * dataset, so the `{ column, values }` shape these calls used to send was
 * silently ignored (texts and charts came back unfiltered).
 */
/** componentIds a dropdown targets: its own list and the build payload's (texts excluded). */
function dropdownTargetsOf(tile: DashboardTile): Set<string> {
  const out = new Set<string>(tile.dropdownControl?.targets ?? []);
  const configured = tile.payload?.['targets'];
  if (Array.isArray(configured)) configured.forEach((target) => {
    if (target && typeof target === 'object') out.add(String((target as Record<string, unknown>)['componentId'] ?? ''));
  });
  out.delete('');
  [...out].forEach((id) => { if (id.startsWith(TEXT_TARGET_PREFIX)) out.delete(id); });
  return out;
}

function computeFilters(filters: ReadonlyArray<{ field: string; values: string[] }>, datasets: ReadonlyArray<string>): Array<Record<string, unknown>> {
  const sets = Array.from(new Set(datasets.filter(Boolean)));
  return filters
    .filter((filter) => filter.field && filter.values.length)
    .flatMap((filter) => sets.map((dataset) => ({ dataset, field: filter.field, operator: 'IN', value: [...filter.values] })));
}

/**
 * Every dataset a chart's definition reads: its `dataset` / `datasets`, and
 * the datasets named on its columns, measures, dimensions and nested-table
 * levels (a columns chart carries them there, not at the top).
 */
function filterDatasets(tile: DashboardTile): string[] {
  const found = new Set(tileDatasets(tile));
  const payload = (tile.payload ?? {}) as Record<string, unknown>;
  const visit = (list: unknown) => {
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const record = item as Record<string, unknown>;
      if (typeof record['dataset'] === 'string' && record['dataset']) found.add(record['dataset']);
      visit(record['columns']);
    });
  };
  ['columns', 'measures', 'dimensions', 'levels'].forEach((key) => visit(payload[key]));
  return [...found];
}

/** The navigation's filters as a list, whichever shape it arrived in. */
function navigationEntriesOf(nav: DashboardNavigationFilter): NavigationFilterEntry[] {
  if (nav.filters?.length) return nav.filters;
  return nav.filterKey && nav.filterValue != null && nav.filterValue !== '' ? [{ key: nav.filterKey, value: nav.filterValue }] : [];
}

const EMPTY_IDS: ReadonlySet<string> = new Set();

/** Text node ids a tile filters (stored on the build as `textTargets`). */
export function textTargetsOf(tile: DashboardTile): string[] {
  const stored = (tile as DashboardTile & { textTargets?: unknown }).textTargets;
  return Array.isArray(stored) ? stored.map(String) : [];
}

export type TextValueState =
  | { status: 'loading' }
  /** `counts`: a "Count per status" answer — every status's count, by name. */
  | { status: 'ready'; value: number | string | null; counts?: Record<string, number> }
  | { status: 'error'; message: string };

/** `message` on a ready state lists measures that failed while others answered. */
/** One edited table cell, as action buttons receive it in `{tableEdits}`. */
export interface TableCellEdit {
  /** The table's layer name. */
  table: string;
  level: number;
  /** Row indices from the top level down to the edited row. */
  path: number[];
  /** A stable row id when the table has one (Normal / Editable tables); else `path` identifies the row. */
  rowKey?: string;
  field: string;
  value: unknown;
  /** The row's own values (keys + measures), so the backend knows which row. */
  row: Record<string, unknown>;
}

/**
 * A nested table's rows are keyed by each column's DISPLAY name ("Customer Name")
 * when it has one, not by its dataset column (detailed_cust_type). Column -> every
 * other name it is shown under, from the table's level queries and levels.
 */
export function nestedColumnAliases(data: Record<string, unknown>): Map<string, string[]> {
  const aliases = new Map<string, Set<string>>();
  const add = (field: unknown, name: unknown) => {
    const column = String(field ?? '').trim();
    const alias = String(name ?? '').trim();
    if (!column || !alias || alias === column) return;
    const key = column.toLowerCase();
    if (!aliases.has(key)) aliases.set(key, new Set());
    aliases.get(key)!.add(alias);
  };
  const queries = Array.isArray(data['levelQueries']) ? (data['levelQueries'] as Array<Record<string, unknown>>) : [];
  queries.forEach((query) => (Array.isArray(query?.['columns']) ? (query['columns'] as Array<Record<string, unknown>>) : [])
    .forEach((column) => add(column?.['field'], column?.['displayName'])));
  const levels = Array.isArray(data['levels']) ? (data['levels'] as Array<Record<string, unknown>>) : [];
  levels.forEach((level) => (Array.isArray(level?.['columns']) ? (level['columns'] as Array<Record<string, unknown>>) : [])
    .forEach((column) => { add(column?.['field'], column?.['label']); add(column?.['field'], column?.['displayName']); }));
  return new Map([...aliases].map(([key, names]) => [key, [...names]]));
}

/**
 * A body template without the column list, or with `["*"]` (every column): the
 * table's configured columns go in `requestData.attributesList` instead.
 */
function withExportColumns(body: unknown, columns: readonly string[]): unknown {
  if (!columns.length || !body || typeof body !== 'object') return body;
  const request = (body as Record<string, unknown>)['requestData'];
  if (!request || typeof request !== 'object' || Array.isArray(request)) return body;
  const data = request as Record<string, unknown>;
  const current = data['attributesList'] ?? data['attributes'] ?? data['columns'];
  const all = current === undefined || current === '*'
    || (Array.isArray(current) && (current.length === 0 || current.every((item) => String(item).trim() === '*')));
  if (!all) return body;
  const { attributes: _a, columns: _c, ...rest } = data;
  return { ...(body as Record<string, unknown>), requestData: { ...rest, attributesList: [...columns] } };
}

/**
 * The columns a table is configured with - what its export sends - for the
 * dataset `entity`: a nested table level's query columns (`levelIndex`), else a
 * table's capability / payload columns. Formula, action and other-dataset
 * columns are left out.
 */
export function exportColumnsOf(data: Record<string, unknown> | null | undefined, entity: string, levelIndex = 0): string[] {
  const source = data ?? {};
  const queries = Array.isArray(source['levelQueries']) ? (source['levelQueries'] as Array<Record<string, unknown>>) : [];
  const caps = (source['capabilities'] ?? {}) as Record<string, unknown>;
  const payload = (source['payload'] ?? {}) as Record<string, unknown>;
  const list = queries.length
    ? (queries[Math.min(levelIndex, queries.length - 1)]?.['columns'] as unknown[] | undefined)
    : Array.isArray(caps['columns']) ? (caps['columns'] as unknown[]) : (payload['columns'] as unknown[] | undefined);
  const same = (dataset: unknown) => !dataset || String(dataset).toLowerCase() === entity.toLowerCase();
  return [...new Set((Array.isArray(list) ? list : [])
    .filter((column): column is Record<string, unknown> => !!column && typeof column === 'object')
    .filter((column) => !column['formula'] && column['isAction'] !== true && same(column['dataset']))
    .map((column) => String(column['field'] ?? '').trim())
    .filter(Boolean))];
}

/** One Excel export (see DesignRuntimeService.exportExcel). */
export interface ExcelExportRun {
  /** The dataset exported. */
  entity: string;
  fileName?: string;
  sheetName?: string;
  /** Blank = the platform API. */
  url?: string;
  /** The table's configured columns (attributesList); empty = every column. */
  columns?: readonly string[];
  /** Body template with {entity} {columns} {filters} {fileName} {sheetName} {userEmail}; blank = the standard request. */
  bodyTemplate?: string;
  /** Columns sent as filters; empty = every dashboard selection on the dataset's columns. */
  filterColumns?: readonly string[];
  /** Rows above the exporting table, top level first (nested child levels). */
  contextRows?: ReadonlyArray<Record<string, unknown>>;
  /** The exporting table's own rows. */
  rows?: ReadonlyArray<Record<string, unknown>>;
  /** Column name -> the other keys its value sits under in rows (header / display names). */
  aliases?: ReadonlyMap<string, readonly string[]>;
}

export interface ExcelFilterSources {
  contextRows?: ReadonlyArray<Record<string, unknown>>;
  rows?: ReadonlyArray<Record<string, unknown>>;
  aliases?: ReadonlyMap<string, readonly string[]>;
  selections: ReadonlyArray<{ field: string; values: readonly string[] }>;
}

/**
 * A chosen filter column's value(s), first found of: the rows above the table
 * (nearest first - the segment, then the customer), the table's own rows (each
 * distinct value), the dashboard's selection. Rows are read by the column name or
 * any alias (a header like "Customer Name" for detailed_cust_type). None = not sent.
 */
export function excelFilterValues(field: string, sources: ExcelFilterSources): string[] {
  const names = [field, ...(sources.aliases?.get(field.toLowerCase()) ?? [])];
  const valueIn = (row: Record<string, unknown>): string => {
    for (const name of names) {
      const value = rowColumnValue(row, name);
      if (value !== undefined && value !== null && typeof value !== 'object' && String(value).trim() !== '') return String(value);
    }
    return '';
  };
  for (const row of [...(sources.contextRows ?? [])].reverse()) {
    const value = valueIn(row);
    if (value) return [value];
  }
  const own = [...new Set((sources.rows ?? []).map(valueIn).filter(Boolean))];
  if (own.length) return own;
  const wanted = field.toLowerCase();
  return [...(sources.selections.find((filter) => filter.field.toLowerCase() === wanted)?.values ?? [])];
}

function editRowKey(edit: Pick<TableCellEdit, 'rowKey' | 'path'>): string {
  return edit.rowKey ?? edit.path.join('.');
}

/** Each edited row once, with every edit applied on top of its loaded values. */
export function editedRowsOf(edits: readonly TableCellEdit[]): Array<Record<string, unknown>> {
  const rows = new Map<string, Record<string, unknown>>();
  edits.forEach((edit) => {
    const key = editRowKey(edit);
    rows.set(key, { ...(rows.get(key) ?? edit.row), [edit.field]: edit.value });
  });
  return [...rows.values()];
}

/** A row's value by column - the exact key, else the same name ignoring case, spaces and underscores
 *  (so "Set Price", "set_price" and "setprice" all find the column). */
export function rowColumnValue(row: Record<string, unknown>, column: string): unknown {
  if (column in row) return row[column];
  const norm = (name: string) => name.toLowerCase().replace(/[\s_-]+/g, '');
  const wanted = norm(column);
  const key = Object.keys(row).find((name) => norm(name) === wanted);
  return key === undefined ? undefined : row[key];
}

function isNegativeNumber(value: unknown): boolean {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return false;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && number < 0;
}

/** `{ uuid, value }` per row; rows without an id are skipped, a repeated id is sent once. */
export function approvalItemsOf(
  rows: ReadonlyArray<Record<string, unknown>>,
  idColumn: string,
  valueColumn: string,
): { items: Array<{ uuid: string; value: unknown }>; missingId: number } {
  const items: Array<{ uuid: string; value: unknown }> = [];
  const seen = new Set<string>();
  let missingId = 0;
  rows.forEach((row) => {
    const uuid = String(rowColumnValue(row, idColumn) ?? '').trim();
    if (!uuid) {
      missingId++;
      return;
    }
    if (seen.has(uuid)) return;
    seen.add(uuid);
    const raw = rowColumnValue(row, valueColumn);
    // A typed number arrives as text ("78000") - send it as a number.
    const value = typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw)) ? Number(raw) : raw ?? null;
    items.push({ uuid, value });
  });
  return { items, missingId };
}

export type ZoneSliderValueState =
  | { status: 'loading' }
  | { status: 'ready'; values: ZoneSliderMeasures; recordId?: string; message?: string }
  | { status: 'error'; message: string };

/** Re-exported so callers of `zoneValues()` need no second import. */
export type { ZoneSliderMeasureKey };

/** A bound text as an aggregated column component definition.
 *
 * The columns shape supports DEAM virtual attributes: compute reads those
 * through the entity layer, evaluates them per row, then applies aggregation.
 * Keep the result under the selected field name so the KPI value extractor can
 * consume the same one-row response shape.
 */
/**
 * Summary "Text - show the value as is": the column's own value straight from
 * the row - words, a sentence, a status - with no aggregate at all (SUM / AVG
 * cast to numbers, turning words into nothing). Rows with no value are left out
 * on the server, so the one row returned is one that has text.
 */
export const TEXT_VALUE_AGGREGATION = 'TEXT_VALUE';

export function textComputePayload(
  componentId: string,
  binding: TextDataBinding,
  title: string,
): Record<string, unknown> {
  // "Count per status": the STATUS_COUNTS chart — one count per value of the
  // group-by column; the text picks its own status out of the answer.
  const textValue = binding.aggregation === TEXT_VALUE_AGGREGATION;
  if (binding.aggregation === STATUS_COUNT_AGGREGATION) {
    const countField = binding.countField?.trim() || 'uuid';
    return {
      componentId,
      componentType: 'CHART',
      chartType: 'STATUS_COUNTS',
      title: title || 'Status counts',
      dataset: binding.dataset,
      groupBy: { field: binding.field, label: binding.field },
      value: { label: 'Count', formula: `COUNT(${countField})` },
    };
  }
  return {
    componentId,
    componentType: 'CHART',
    // The backend registers bound text under its own type. The value is still
    // read with the KPI extractor — one aggregated number, same shape.
    chartType: 'TEXT',
    title: title || 'Value',
    columns: [{
      dataset: binding.dataset,
      // A column name, or a formula over columns ("[target_price] - [set_price]") the backend aggregates.
      field: binding.field,
      displayName: textResultKey(binding),
      // "Text - show the value as is": no aggregate - the column as stored.
      ...(textValue ? {} : { aggregation: binding.aggregation || 'SUM' }),
      // "Only rows where …": the compute API adds it to the query's WHERE.
      ...(textRowFilter(binding) ?? {}),
    }],
    ...(textValue
      ? {
          // Skip rows with no value (`!= ''` leaves out NULL and blank alike),
          // and one row is all a text shows. Callers ADD the dashboard filters.
          filters: [{ dataset: binding.dataset, field: binding.field, operator: '!=', value: '' }],
          pagination: { enabled: true, pageSize: 1 },
        }
      : {}),
  };
}

/** A compute payload with the dashboard's filters added to its own (never replacing them). */
function withFilters(payload: Record<string, unknown>, filters: Array<Record<string, unknown>>): Record<string, unknown> {
  const own = Array.isArray(payload['filters']) ? payload['filters'] as Array<Record<string, unknown>> : [];
  const all = [...own, ...filters];
  return all.length ? { ...payload, filters: all } : payload;
}

/** Whether a bound text's `field` is a formula over columns rather than one column name. */
export function isTextFormula(field: string | undefined): boolean {
  const text = String(field ?? '').trim();
  return !!text && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(text);
}

/**
 * The key a bound text's value comes back under: its column, or "value" for a
 * formula - a long formula as a column name would be cut at Postgres' 63 characters.
 */
/**
 * A bound text's "Only rows where" as a column's default filter (the backend
 * reads it like a table column's / dropdown's): `in` (the default) lists every
 * value, eq / neq / contains use the first, empty / not_empty need none
 * ("null" with eq / neq means empty / not empty - the backend maps it).
 * Null when nothing is set.
 */
export function textRowFilter(binding: Pick<TextDataBinding, 'filterField' | 'filterOperator' | 'filterValues'>):
  { filterField: string; filterOperator: string; filterValue: string | string[] } | null {
  const field = binding.filterField?.trim();
  if (!field) return null;
  const operator = (binding.filterOperator || 'in').trim().toLowerCase();
  if (operator === 'empty' || operator === 'not_empty') return { filterField: field, filterOperator: operator, filterValue: '' };
  const values = (binding.filterValues ?? []).map((value) => String(value).trim()).filter(Boolean);
  if (!values.length) return null;
  if (operator === 'in') return { filterField: field, filterOperator: 'IN', filterValue: values };
  return { filterField: field, filterOperator: operator, filterValue: values[0] };
}

export function textResultKey(binding: Pick<TextDataBinding, 'field'> | undefined): string {
  const field = binding?.field ?? '';
  return isTextFormula(field) ? 'value' : field;
}

/**
 * Every status's count out of a STATUS_COUNTS answer:
 * `data: [{ statusCounts: [{ department: 'planned', value: 1 }, …] }]` → `{ planned: 1, … }`.
 */
export function statusCountsOf(result: Record<string, unknown>): Record<string, number> {
  const counts: Record<string, number> = {};
  const data = Array.isArray(result?.['data']) ? (result['data'] as unknown[]) : [result];
  for (const entry of data) {
    const list = (entry as Record<string, unknown> | null)?.['statusCounts'];
    if (!Array.isArray(list)) continue;
    for (const item of list as Array<Record<string, unknown>>) {
      const name = String(item?.['department'] ?? item?.['status'] ?? item?.['name'] ?? '');
      const value = Number(item?.['value'] ?? item?.['count']);
      if (name && Number.isFinite(value)) counts[name] = (counts[name] ?? 0) + value;
    }
  }
  return counts;
}

/** The `result` addressed to `componentId`, from either compute response shape. */
function computeEntryFor(response: Record<string, unknown>, componentId: string): Record<string, unknown> {
  const results = response['results'];
  if (!Array.isArray(results) || !results.length) return response;
  const list = results as Array<Record<string, unknown>>;
  const match = list.find((entry) => {
    const result = (entry['result'] ?? entry) as Record<string, unknown>;
    return String(entry['componentId'] ?? result['componentId'] ?? '') === componentId;
  }) ?? list[0];
  return (match['result'] ?? match) as Record<string, unknown>;
}

/** The backend reports a bad component as a row `{ error: … }`, not an HTTP error. */
/**
 * The compute answered this component with NO rows (an empty list, not an
 * error): the filters left it nothing to show. A component the compute did not
 * recompute carries no list at all.
 */
function isExplicitEmptyAnswer(entry: Record<string, unknown>): boolean {
  const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
  if (!result || computeEntryError(result)) return false;
  const data = result['data'];
  const rows = Array.isArray(data) ? data : (data as { data?: unknown } | null | undefined)?.['data'];
  if (Array.isArray(rows)) return rows.length === 0;
  const list = result['responseList'];
  return Array.isArray(list) && list.length === 0;
}

function computeEntryError(result: Record<string, unknown>): string {
  const data = result['data'];
  const row = Array.isArray(data) ? data[0] : data;
  const message = (row as Record<string, unknown> | null | undefined)?.['error'];
  return typeof message === 'string' ? message : '';
}

/** The single number out of a KPI compute result, via the shared extractor. */
function kpiValueOf(result: Record<string, unknown>, field: string): number | string | null {
  const widgetData = buildKpiWidgetData(result, { widgetId: 'text', kpiField: field });
  const rows = (widgetData['data'] as { data?: Array<Record<string, unknown>> } | undefined)?.data;
  return (rows?.[0]?.['value'] as number | string | null | undefined) ?? null;
}

/** A single-tile compute response in the `{ results: [...] }` shape applyResults reads. */
function computeResults(response: Record<string, unknown>, tile: DashboardTile): Record<string, unknown> {
  if (Array.isArray(response['results'])) return { ...response, results: (response['results'] as Array<Record<string, unknown>>).map(entry =>
    String(entry['componentId']) === tile.componentId
      ? { ...entry, result: { chartType: tile.layoutType, ...(entry['result'] as Record<string, unknown> ?? {}) } }
      : entry) };
  const entry = response['result']
    ? { ...response, componentId: response['componentId'] ?? tile.componentId,
        result: { chartType: tile.layoutType, ...(response['result'] as Record<string, unknown>) } }
    : { componentId: tile.componentId, result: { chartType: tile.layoutType, ...response, componentId: tile.componentId } };
  return { results: [entry] };
}

/** True when render data still holds rows — i.e. any key publishing strips is set. */
function hasRows(data: Record<string, unknown> | null): boolean {
  if (!data) return false;
  return WIDGET_ROW_KEYS.some((key) => {
    const value = data[key];
    return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null;
  });
}

/**
 * How a dashboard refresh reaches a text / zone slider: `quiet` keeps the
 * shown value until the new one lands; `stamp` is the refresh's start on the
 * request clock (see `DesignRuntimeService.supersededSince`).
 */
export interface QuietRefresh {
  quiet: boolean;
  stamp: number;
}

interface ApplyResultsOptions {
  /** A refresh answer: leaves interaction busy state alone and marks the tiles as refreshed. */
  refresh?: boolean;
  /** The tile id this answer was asked for alone: an explicit empty table answer is real. */
  answerFor?: string;
  /**
   * The stamp of the interaction that asked (beginRecompute). A component a
   * NEWER interaction has since asked for keeps waiting for that answer: this
   * older one is dropped for it. Without this, "select a region, then clear it"
   * could land the filtered answer last and leave the table filtered.
   */
  stamp?: number;
}

/** A table's compute entry that answered successfully with no rows (`data: []`). */
function isExplicitEmptyTable(entry: Record<string, unknown>, target: DashboardTile): boolean {
  if (target.layoutType !== 'TABLE_MATRIX') return false;
  const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
  const data = result?.['data'];
  return Array.isArray(data) && data.length === 0 && !computeEntryError(result);
}

function targetIdsOf(targets: unknown): string[] {
  return (Array.isArray(targets) ? (targets as Array<Record<string, unknown>>) : [])
    .map((target) => String(target?.['componentId'] ?? ''))
    .filter(Boolean);
}

// ── Button helpers ───────────────────────────────────────────────────────────

/** Key prefix of a button's entry in the active filter map (buttons have no tile). */
const BUTTON_FILTER_PREFIX = 'button:';

/** Widgets whose value a "Save changes" button can send (see VALUE_SOURCE_LAYOUT_TYPES). */
const VALUE_SOURCE_LAYOUTS: ReadonlySet<string> = new Set(VALUE_SOURCE_LAYOUT_TYPES);

/** Where a zone slider's handle is (see DesignRuntimeService.zoneSliderLevel). */
export interface ZoneSliderLevelNow {
  sliderId: string;
  /** The block's label, e.g. "Level 2 — Director Approval"; '' outside every block. */
  label: string;
  /** 0-based, left to right; -1 outside every block. */
  index: number;
  color: string;
  value: number;
  valueText: string;
  /** Every block's label, left to right. */
  levels: string[];
}

export interface ButtonRunState {
  busy: boolean;
  /** A toggle filter is applied. */
  active: boolean;
  message: string;
  kind: 'ok' | 'error' | '';
}

const EMPTY_BUTTON_STATE: ButtonRunState = { busy: false, active: false, message: '', kind: '' };

/** The first list of row objects in a widget's render data (table rows, a chart's static rows…). */
export function rowsIn(data: unknown, depth = 0): Array<Record<string, unknown>> {
  if (Array.isArray(data)) {
    return data.length && data.every((row) => row && typeof row === 'object' && !Array.isArray(row))
      ? (data as Array<Record<string, unknown>>)
      : [];
  }
  if (!data || typeof data !== 'object' || depth > 3) return [];
  const record = data as Record<string, unknown>;
  const preferred = ['staticData', 'rows', 'tableData', 'data', 'items', 'results'];
  for (const key of [...preferred, ...Object.keys(record).filter((key) => !preferred.includes(key))]) {
    const rows = rowsIn(record[key], depth + 1);
    if (rows.length) return rows;
  }
  return [];
}

export function rowsToCsv(rows: Array<Record<string, unknown>>): string {
  const columns: string[] = [];
  rows.forEach((row) => Object.keys(row).forEach((key) => { if (!columns.includes(key)) columns.push(key); }));
  const cell = (value: unknown): string => {
    const text = value === null || value === undefined ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  return [columns.map(cell).join(','), ...rows.map((row) => columns.map((column) => cell(row[column])).join(','))].join('\r\n');
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function withExtension(name: string, extension: string): string {
  const base = String(name ?? '').trim() || 'export';
  return /\.[a-z0-9]{2,5}$/i.test(base) || !extension ? base : `${base}.${extension}`;
}

function extensionForType(type: string): string {
  if (/json/i.test(type)) return 'json';
  if (/csv/i.test(type)) return 'csv';
  if (/spreadsheet|excel/i.test(type)) return 'xlsx';
  if (/pdf/i.test(type)) return 'pdf';
  if (/zip/i.test(type)) return 'zip';
  if (/text\/plain/i.test(type)) return 'txt';
  return '';
}

function fileNameFromDisposition(header: string | null): string {
  if (!header) return '';
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded) {
    try { return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, '')); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : '';
}

/** A button API's optional headers, typed as a JSON object; anything else is ignored. */
function parseHeaders(raw: string): HttpHeaders | undefined {
  const text = String(raw ?? '').trim();
  if (!text) return undefined;
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
    return new HttpHeaders(Object.fromEntries(Object.entries(parsed).map(([key, value]) => [key, String(value)])));
  } catch {
    console.warn('[dvm] button API headers are not a JSON object; sending none:', text);
    return undefined;
  }
}

/**
 * A dropdown as saved: without the option list it was saved with. Options
 * always come from the compute API (hydrateDropdowns), so a stale saved list
 * is never shown or used.
 */
function withoutSavedOptions(tile: DashboardTile | null): DashboardTile | null {
  if (!tile || tile.layoutType !== 'DROPDOWN' || !tile.dropdownControl?.options?.length) return tile;
  return { ...tile, dropdownControl: { ...tile.dropdownControl, options: [] } };
}

/**
 * A dropdown's options from a `/components/compute` answer: the entry for its
 * componentId (else the only one), `result.data` rows - `{ value }` as the
 * backend sends them, or keyed by the field - or an explicit option list.
 * Error rows ("No component found …") list nothing.
 */
function computedDropdownOptions(response: Record<string, unknown> | null, componentId: string, field: string): string[] {
  const results = Array.isArray(response?.['results']) ? response!['results'] as Array<Record<string, unknown>> : [];
  const entry = results.find((item) => String(item?.['componentId'] ?? '') === componentId) ?? (results.length === 1 ? results[0] : undefined);
  if (!entry) return [];
  const result = (entry['result'] ?? entry) as Record<string, unknown>;
  const explicit = dropdownOptionsFromComputeResponse(result, field);
  if (explicit.length) return explicit;
  const rows = Array.isArray(result['data']) ? result['data'] as unknown[] : [];
  const values = rows.map((row) => {
    if (row === null || row === undefined) return '';
    if (typeof row !== 'object') return String(row);
    const record = row as Record<string, unknown>;
    if ('error' in record) return '';
    const value = record[field] ?? record['value'];
    return value === null || value === undefined ? '' : String(value);
  }).filter((value) => value !== '');
  return [...new Set(values)];
}

/**
 * A bound text's value from its compute result - the ONE reading used by its
 * own compute, filter changes and refreshes alike: an error, a "Count per
 * status" text's counts (and its own status's count), or the plain KPI value.
 */
function textValueFromResult(binding: TextDataBinding, result: Record<string, unknown>): TextValueState {
  const error = computeEntryError(result);
  if (error) return { status: 'error', message: error };
  if (binding.aggregation === STATUS_COUNT_AGGREGATION) {
    const counts = statusCountsOf(result);
    return { status: 'ready', value: statusCountOf(counts, binding.statusValue), counts };
  }
  return { status: 'ready', value: kpiValueOf(result, textResultKey(binding)) };
}

/**
 * Components a compute call needs in the backend's store: its targets AND every
 * other input it sends (`otherInputs`). An unregistered other input is not an
 * error there - the backend guesses its column from the title ("Customer Name"
 * → customer_name) and its filter quietly misses - so register it first.
 */
function registeredIdsFor(payload: Record<string, unknown>, targetIds: string[]): string[] {
  const others = Array.isArray(payload['otherInputs'])
    ? (payload['otherInputs'] as Array<Record<string, unknown>>).map((input) => String(input?.['componentId'] ?? ''))
    : [];
  return [...new Set([...targetIds, ...others].filter(Boolean))];
}

/** Prefix of a "Color by value" limit that reads a dataset column instead of a marker. */
export const COLUMN_LIMIT_PREFIX = 'col:';

/** `col:<dataset>|<field>|<aggregation>` - a range limit taken from a dataset column. */
export function columnLimitId(dataset: string, field: string, aggregation = 'SUM'): string {
  return `${COLUMN_LIMIT_PREFIX}${dataset}|${field}|${aggregation || 'SUM'}`;
}

export function parseColumnLimit(id: string): { dataset: string; field: string; aggregation: string } | null {
  if (!id.startsWith(COLUMN_LIMIT_PREFIX)) return null;
  const [dataset, field, aggregation] = id.slice(COLUMN_LIMIT_PREFIX.length).split('|');
  return dataset && field ? { dataset, field, aggregation: aggregation || 'SUM' } : null;
}

/**
 * A picked file's bytes, read the way the Excel add-in reads a workbook
 * (excel_plugin_custom workbook-stream.service readOfficeFile): 4 MB slices
 * joined into one Uint8Array - Excel, PDF or any other file.
 */
async function readFileBytes(file: File, sliceSize = 4 * 1024 * 1024): Promise<Uint8Array> {
  const chunks: Uint8Array[] = [];
  let totalLength = 0;
  for (let start = 0; start < file.size; start += sliceSize) {
    const chunk = new Uint8Array(await file.slice(start, start + sliceSize).arrayBuffer());
    chunks.push(chunk);
    totalLength += chunk.byteLength;
  }
  const bytes = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}

/** The Upload default body before it sent "fileBytes" (whitespace removed). */
const LEGACY_UPLOAD_BODY = '{"requestData":{"operation":"upload","entityName":"{entity}","businessKeys":{businessKeys},"fileName":"{fileName}","file":{fileBytes}}}';

/** The platform request around an upload's `requestData`: the attachment service runs it. */
const UPLOAD_ENVELOPE: Readonly<Record<string, unknown>> = {
  responseList: [],
  entity: 'attachment',
  workflowId: 'UI',
  endpoints: [{ functionName: 'SecureRecordAttachmentService', version: '1.0' }],
  communicationType: 'rest',
  version: '1.0',
};

/**
 * An upload body with `requestData` gets every envelope key it lacks; a key the
 * body sets itself is kept. Any other body (a custom API's) is sent as written.
 */
export function withUploadEnvelope(body: unknown): unknown {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body;
  const request = body as Record<string, unknown>;
  const data = request['requestData'];
  if (!data || typeof data !== 'object') return body;
  const missing = Object.entries(UPLOAD_ENVELOPE).filter(([key]) => request[key] === undefined);
  return missing.length ? { ...request, ...Object.fromEntries(missing.map(([key, value]) => [key, structuredClone(value)])) } : body;
}

/** The file's content type: the browser's, else from the extension (Excel / PDF / CSV). */
function fileMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ({
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    pdf: 'application/pdf',
    csv: 'text/csv',
  } as Record<string, string>)[ext] ?? 'application/octet-stream';
}
